import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  IconButton,
  Icon,
  useDisclosure,
  useToast,
  Spinner,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Image,
  useBreakpointValue,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import {
  FiSettings,
  FiWifi,
  FiWifiOff,
  FiX,
  FiXCircle,
  FiLogIn,
  FiLogOut,
  FiInfo,
  FiCheckCircle,
  FiRefreshCw,
  FiUploadCloud,
  FiCpu,
  FiBookmark,
  FiZap,
  FiCamera,
  FiHardDrive,
  FiSearch,
} from 'react-icons/fi';
import QRScanner from './QRScanner';
import USBScanner from './USBScanner';
import ManualScanner from './ManualScanner';
import ConsultaSearchBox from './ConsultaSearchBox';
import StudentInfoCard from './StudentInfoCard';
import ExitSummaryView from './ExitSummaryView';
import ScannerConfig from './ScannerConfig';
import HistoryPanel from './HistoryPanel';
import { getShellBg, WarmShapes } from './scannerTheme';
import ModalAddSurveyResponse from 'components/Modal/ModalAddSurveyResponse';
import ModalSurveyResponse from 'components/Modal/ModalSurveyResponse';
import { loadSavedScannerId, saveScannerId, clearScannerId, loadHistoryPanelCollapsed, saveHistoryPanelCollapsed, saveLastInputMode } from '../scannerStorage';
import { playConfirmSound } from '../confirmSound';
import { validate, logAccess, listScanners, upsertPedagogicalExit, upsertStudentStatus } from 'services/qrService';
import { submitSurveyResponse, getSurveyResponse, API_URL_BASE } from 'services/surveyService';
import { buildFileUrl } from 'components/Fichas/fichaFieldRenderer';
import useStudentsCache from 'hooks/useStudentsCache';
import useOfflineQueue  from 'hooks/useOfflineQueue';
import { useUserAuth, useCanOpenScanner } from 'utils/site';
import { normalizeCourseName, codeToDisplayLabel } from 'utils/courseUtils';
import { useAppContext } from 'context/appContext';
import { SIDEBAR_W_EXPANDED, SIDEBAR_W_COLLAPSED } from 'components/Sidebar/sidebarSize';

// "Validación" (event_ticket) existía como cuarta categoría, pero
// ScannerConfig.js ya la excluía de CATEGORIES (no hay lectores activos de
// ese tipo) — se deja fuera también acá; si algún lector viejo quedara
// configurado con ese modo, cae al fallback `?? 'entry'`/`MODE_BADGE.access_log`.
const modeToAccessType = {
  access_log: 'entry',
  exit_log:   'exit',
  info_only:  'info',
};

// Mismos colores que usa ScannerConfig.js para las categorías Entrada/
// Salida/Consulta — así el switch Entrada/Salida, el botón redondo de
// Consulta y la tarjeta de resultado hablan el mismo lenguaje visual.
// Salida usa el tono ámbar (el mismo de las alertas de sincronización
// pendiente), que naturalmente destaca más que los otros — es la acción
// más sensible (alumnos saliendo del colegio).
const MODE_BADGE = {
  access_log: { icon: FiLogIn, label: 'Entrada',  bg: 'mint.bg', fg: 'mint.ink' },
  exit_log:   { icon: FiLogOut, label: 'Salida',  bg: 'butter.bg', fg: 'butter.ink' },
  info_only:  { icon: FiInfo,  label: 'Consulta', bg: 'sky.bg', fg: 'sky.ink' },
};

// Degradado (mismo estilo que WARM_SHELL_BG y que la columna de tipo del
// panel de historial, ver ACCESS_TYPE_BG en HistoryPanel.js) usado como
// fondo del switch Entrada/Salida — cambia de color según el modo activo,
// igual que ya hacía el ícono chico de adentro.
const MODE_GRADIENT = {
  access_log: 'linear-gradient(160deg, #E7F7EE 0%, #C7ECD7 100%)',
  exit_log:   'linear-gradient(160deg, #FDF1DD 0%, #F6DBAB 100%)',
  info_only:  'linear-gradient(160deg, #EAF2FD 0%, #CEE0FA 100%)',
};

// Usado por handleToggleAccessMode para el toast cuando falta un lector del
// tipo objetivo — separado de MODE_BADGE.label porque acá solo aplican
// Entrada/Salida (Consulta tiene su propio mensaje en handleToggleConsulta).
const ACCESS_MODE_LABEL = { access_log: 'Entrada', exit_log: 'Salida' };

// Dispositivo de lectura — independiente del "lector" (config.mode/ubicación)
// de arriba. Cambiar acá NO afecta qué se registra, solo CÓMO se captura el
// código del alumno.
const SCAN_MODES = [
  { key: 'camera', label: 'Cámara', icon: FiCamera },
  { key: 'usb',    label: 'USB',    icon: FiHardDrive },
  { key: 'manual', label: 'Manual', icon: FiSearch },
];

// Misma ruta que abren los botones "Abrir escáner"/"Abrir lector USB"/"Abrir
// modo manual" (ver AccessControl/index.js) — así la barra de direcciones
// queda consistente con lo que se ve en pantalla tras cambiar de modo, y un
// refresh vuelve a abrir en el modo correcto en vez de resetear a "cámara".
const SCAN_MODE_PATHS = {
  camera: '#/admin/scanner',
  usb:    '#/admin/scanner-usb',
  manual: '#/admin/scanner-manual',
};

const MAX_HISTORY = 10;

// Returns true when current time is before the scanner's scheduled end time
const detectEarlyExit = (scannerConfig, now = new Date()) => {
  if (scannerConfig?.mode !== 'exit_log') return false;
  if (!scannerConfig?.config?.scheduled_end_time) return false;
  const [h, m] = scannerConfig.config.scheduled_end_time.split(':').map(Number);
  const end = new Date(now);
  end.setHours(h, m, 0, 0);
  return now < end;
};

// El backend (resolveStudentStatus) manda ends_at como "HH:mm" (sin fecha) para
// salidas pedagógicas — se asume siempre del día de hoy, igual que
// scheduled_end_time. No manda starts_at para este estado, así que solo se
// valida el límite superior: si ya pasó esa hora, la salida ya no está vigente.
const isPedagogicalExitActive = (status, now = new Date()) => {
  if (status?.type !== 'pedagogical_exit') return false;
  if (!status.ends_at) return true;
  const [h, m] = status.ends_at.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return true;
  const end = new Date(now);
  end.setHours(h, m, 59, 999);
  return now <= end;
};

// Identifica de forma estable UNA salida pedagógica (por grupo o por estado
// individual, ver AccessControlController::resolveStudentStatus) — se usa
// para saber si dos alumnos escaneados seguidos pertenecen a la misma salida
// y así reusar la ficha de responsable ya firmada, en vez de pedirla de nuevo.
const pedagogicalExitKey = (status) =>
  status?.type === 'pedagogical_exit' && status.id != null
    ? `${status.source ?? 'unknown'}:${status.id}`
    : null;

// Mismo propósito que pedagogicalExitKey, para Salida por Enfermedad — acá
// no hay reuso entre alumnos distintos (la salida es individual), pero sí
// sirve para no volver a pedir la ficha si se re-escanea al mismo alumno.
const sickExitKey = (status) =>
  status?.type === 'sick_exit' && status.id != null
    ? `${status.source ?? 'unknown'}:${status.id}`
    : null;

// Arma la misma forma { survey: { name }, responses: [{ answer, question }] }
// que devuelve el servidor tras guardar de verdad, pero a partir de un
// borrador todavía no enviado (ModalAddSurveyResponse con deferSubmit=true) —
// así el botón "Ver" funciona igual antes y después de confirmar la salida.
const buildPreviewFromDraft = (draft) => {
  if (!draft) return null;
  // String(...) en ambos lados a propósito — question.id llega numérico desde
  // la API, pero answer.question_id sale como string (ModalAddSurveyResponse
  // arma answers recorriendo `for (key in data)`, y las keys de un objeto/
  // los nombres de campo de formulario SIEMPRE son string, incluso si
  // `register(`${question.id}`)` partió de un id numérico). Sin esto,
  // Map.get() nunca matcheaba (123 !== "123") y CADA respuesta quedaba con
  // question: null — por eso no se veía ni la etiqueta ni el tipo (así que
  // tampoco se detectaba "signature" para mostrar la firma como imagen).
  const questionsById = new Map((draft.survey?.questions ?? []).map(q => [String(q.id), q]));
  return {
    survey: { name: draft.survey?.name },
    responses: (draft.payload?.answers ?? []).map((a, idx) => ({
      id: `draft-${idx}`,
      answer: a.answer,
      question: questionsById.get(String(a.question_id)) ?? null,
    })),
  };
};

// Fichas armadas con deferSubmit (Retiro Anticipado / Salida Pedagógica) que
// todavía NO se enviaron de verdad al servidor viven solo en memoria (React
// state) hasta tocar "Confirmar" — un refresh de página las perdía por
// completo aunque la salida en sí (programada o creada al vuelo) siguiera
// existiendo en la base de datos, obligando a rehacer todo el formulario.
// Se respaldan en sessionStorage (datos de esta sesión de escaneo, no algo
// que deba sobrevivir días) con una expiración corta.
const PENDING_FICHA_PREFIX = 'scanner-pending-ficha:';
const PENDING_FICHA_MAX_AGE_MS = 60 * 60 * 1000; // 1 hora

const savePendingFicha = (key, data) => {
  if (!key) return;
  try {
    sessionStorage.setItem(PENDING_FICHA_PREFIX + key, JSON.stringify({ data, savedAt: Date.now() }));
  } catch {}
};

const loadPendingFicha = (key) => {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(PENDING_FICHA_PREFIX + key);
    if (!raw) return null;
    const { data, savedAt } = JSON.parse(raw);
    if (!data || Date.now() - savedAt > PENDING_FICHA_MAX_AGE_MS) {
      sessionStorage.removeItem(PENDING_FICHA_PREFIX + key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const clearPendingFicha = (key) => {
  if (!key) return;
  try { sessionStorage.removeItem(PENDING_FICHA_PREFIX + key); } catch {}
};

// Alto real del AdminNavbar fijo (position="fixed", ver
// components/Navbars/AdminNavbar.js) — es lo único que este shell necesita
// despejar arriba. Antes se usaba un valor mayor (70-75px) para compensar
// además el padding-top del PanelContainer del layout (68px, ver
// theme/additions/layout/PanelContainer.js + el override en layouts/Admin.js),
// pero el m="-30px" de abajo solo cancelaba 30px de esos 68px — el resto (38px)
// quedaba como aire residual SUMADO a este offset. Ahora mt cancela ese
// padding-top exacto, así este offset es el alto del navbar (60px) + un
// pequeño respiro (8px) para que el header no quede pegado al navbar.
const NAVBAR_OFFSET = '68px';
// padding-top real del PanelContainer que envuelve esta vista (Admin.js lo
// fija en 68px, sin variar por breakpoint) — se cancela con mt para que
// NAVBAR_OFFSET sea el único aire que queda arriba.
const PANEL_PT = '68px';
// Alto compartido por los chips de la columna izquierda (dispositivo de
// lectura/auto-confirmar) y por el ícono del tag de tipo (Entrada/Salida/...)
// del selector de lector, para que ambos queden a la misma altura visual.
// Agrandado para uso táctil rápido en portería (antes 38px).
const CHIP_H = '48px';
// Switch Entrada/Salida — más alto que CHIP_H a propósito (ahora lleva 2
// líneas de texto: ENTRADA/SALIDA + nombre del lector) y con la perilla
// calculada explícitamente en vez de números sueltos: `border-box` (default
// de Chakra) hace que el borde de 2px del switch reste alto/ancho del área
// interna, así que el margen de la perilla tiene que descontarlo también —
// si no, la perilla queda tocando el borde de abajo por dentro en vez de
// tener aire parejo arriba y abajo (ver el comentario en el JSX del switch).
const SWITCH_H = '58px';
const SWITCH_KNOB_SIZE = '40px';
const SWITCH_KNOB_MARGIN = '7px';

const ScannerShell = ({ inputMode: inputModeProp = 'camera' }) => {
  // Estado local (no solo prop) para poder cambiar cámara/USB/manual desde
  // dentro de la pantalla — inputModeProp solo siembra el valor inicial de
  // la ruta con la que se abrió (nunca cambia durante la vida del componente,
  // así que no hace falta un efecto de sincronización).
  const [inputMode, setInputMode] = useState(inputModeProp);
  const [phase, setPhase]         = useState('scanning');
  useEffect(() => { saveLastInputMode(inputMode); }, [inputMode]);
  const [student, setStudent]     = useState(null);
  const [studentStatus, setStudentStatus] = useState(null); // { type, reason, notes, starts_at, ends_at, title, authorized_by }
  const [pedagogicalExitActive, setPedagogicalExitActive] = useState(false);
  const [isEarlyExit, setIsEarlyExit]     = useState(false);
  const [isOfflineResult, setIsOfflineResult] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [config, setConfig]       = useState(null);
  const [remembered, setRemembered] = useState(false);
  // Lectores activos de TODOS los tipos (no solo el recordado para este
  // inputMode) — a diferencia de `config`, esto alimenta el switch
  // Entrada/Salida y el botón redondo de Consulta para saber, sin abrir
  // ningún modal, si hay 0/1/2+ lectores de cada categoría. Se recarga junto
  // con el refresco periódico de `config` más abajo.
  const [allScanners, setAllScanners] = useState([]);
  // true mientras el botón redondo de Consulta está "activo": `config` fue
  // reemplazado temporalmente por un lector de tipo Consulta (info_only) y
  // el lector de Entrada/Salida real quedó guardado en
  // consultaPrevConfigRef, esperando a que goToScanning() lo restaure (al
  // cerrar la tarjeta de info o al cancelar la consulta a mano).
  const [isConsultaActive, setIsConsultaActive] = useState(false);
  const consultaPrevConfigRef = useRef(null);
  // Último lector usado por categoría (Entrada/Salida/Consulta) en esta
  // sesión — cuando hay 2+ lectores activos del mismo tipo, el switch y el
  // botón de Consulta reusan este valor en vez de interrumpir siempre con
  // un picker, mientras el elegido siga existiendo. Se reinicia en cada
  // carga de página (a diferencia de "Recordar", que sí persiste — ver
  // `remembered`/handleToggleRemember, ahora compartido por Entrada/Salida
  // y Consulta por igual).
  const lastUsedByModeRef = useRef({});
  // Categoría (access_log/exit_log/info_only) que necesita desempate manual
  // ahora mismo — no-null abre un <ScannerConfig forcedCategory=...> chico
  // que salta directo a la lista de esa categoría, sin la grilla completa.
  const [pendingPickerCategory, setPendingPickerCategory] = useState(null);
  // Opción temporal: confirma automáticamente apenas se valida un alumno,
  // sin esperar a que el operador toque "Confirmar". No se persiste — arranca
  // apagada en cada carga, como Recordar. Se guarda como { value, pulse } en
  // vez de un boolean simple: "pulse" sube en el mismo setState que "value"
  // (cambio atómico) cada vez que el estado efectivamente cambia, sea por un
  // click manual o por la auto-activación de Entrada/salida pedagógica (ver
  // efectos más abajo) — el botón "Auto" lo usa como key para re-disparar su
  // micro-animación de encendido/apagado incluso cuando nadie lo tocó.
  const [autoConfirmState, setAutoConfirmState] = useState({ value: false, pulse: 0 });
  const autoConfirm = autoConfirmState.value;
  const setAutoConfirm = useCallback((value) => {
    setAutoConfirmState(prev => {
      const next = typeof value === 'function' ? value(prev.value) : value;
      if (next === prev.value) return prev;
      return { value: next, pulse: prev.pulse + 1 };
    });
  }, []);
  // Segundos restantes antes de que "Auto" confirme solo — null cuando no
  // hay ninguna cuenta regresiva corriendo (Auto apagado, no está en
  // 'preview', o la acción de esta tarjeta requiere al operador). Se
  // muestra en StudentInfoCard (StatusBanner) para que el operador sepa
  // cuánto le queda para tocar "Cancelar"/"Cerrar" si no corresponde.
  const [autoCountdown, setAutoCountdown] = useState(null);
  const [isOnline, setIsOnline]   = useState(navigator.onLine);
  const [history, setHistory]     = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  // Ficha "Retiro Anticipado" YA LLENA pero todavía NO enviada al servidor —
  // { payload, site, surveySlug, survey }, armado por ModalAddSurveyResponse
  // con deferSubmit=true. Solo se convierte en un survey_response real
  // (submitSurveyResponse) en el momento de tocar "Confirmar salida
  // anticipada" — así, si el operador cancela antes, no queda ninguna ficha
  // huérfana guardada en el servidor.
  const [pickupSurveyDraft, setPickupSurveyDraft] = useState(null);
  // Snapshot de la ficha YA enviada de verdad — solo existe después de
  // confirmar (o al reusar la de otro alumno del mismo grupo, ver abajo).
  const [pickupSurveyPreview, setPickupSurveyPreview] = useState(null);

  // Mismo esquema para "Salida Pedagógica - Responsable", con un matiz: una
  // vez confirmada para el primer alumno de un grupo, el id real (no el
  // borrador) se reutiliza para el resto — por eso acá SÍ existe un id
  // (pedagogicalSurveyResponseId) además del borrador.
  const [pedagogicalSurveyDraft, setPedagogicalSurveyDraft] = useState(null);
  const [pedagogicalSurveyResponseId, setPedagogicalSurveyResponseId] = useState(null);
  const [pedagogicalSurveyPreview, setPedagogicalSurveyPreview] = useState(null);
  // Encargado (acompaña la salida) y quien la autoriza — elegidos vía
  // StaffPicker en StudentInfoCard, se usan en handleConfirmPedagogicalActive
  // para atribuir la ficha a personas reales en vez de al alumno escaneado.
  const [pedagogicalEncargado, setPedagogicalEncargado] = useState(null);
  const [pedagogicalAuthorizedBy, setPedagogicalAuthorizedBy] = useState(null);

  // Mismo esquema para "Salida por Enfermedad - Responsable" — ver el bloque
  // pedagogicalSurveyDraft/pedagogicalEncargado de arriba, misma lógica pero
  // para la salida individual por enfermedad (sin reuso entre alumnos).
  const [sickSurveyDraft, setSickSurveyDraft] = useState(null);
  const [sickSurveyResponseId, setSickSurveyResponseId] = useState(null);
  const [sickSurveyPreview, setSickSurveyPreview] = useState(null);
  const [sickEncargado, setSickEncargado] = useState(null);
  const [sickAuthorizedBy, setSickAuthorizedBy] = useState(null);
  // Pantalla de resumen tras confirmar una ficha RECIÉN firmada (no cuando
  // se reusa una ya existente) — { kind: 'pickup'|'pedagogical'|'sick',
  // student, courseLabel?, fichaItem, customer }, ver _doLog más abajo.
  const [exitSummary, setExitSummary] = useState(null);
  const { user: currentStaffUser } = useUserAuth();
  // Solo administrador e inspector pueden operar el escáner — ver
  // useCanOpenScanner (utils/site.js). Necesario acá (no solo ocultando el
  // link/ícono) porque dashRoutes (routes.js), que es lo que realmente monta
  // estas rutas en layouts/Admin.js, no filtra por rol: sin este guard,
  // cualquiera podría entrar tecleando /admin/scanner directamente.
  const canOpenScanner = useCanOpenScanner();
  // Ancho real del sidebar fijo (240px expandido / 76px colapsado a riel de
  // íconos, ver Sidebar/index.js) — el badge de conexión fijo de abajo lo
  // necesita para calcular su offset "left" correctamente en ambos estados;
  // sin esto quedaría desalineado cada vez que alguien colapsa/expande el
  // sidebar (mismo tipo de bug ya corregido antes para el propio panel de
  // historial y el AdminNavbar).
  const { sidebarCollapsed } = useAppContext();
  const sidebarWidthPx = sidebarCollapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W_EXPANDED;
  // Una salida pedagógica autoriza a TODO un curso/turno — la firma del
  // responsable se pide una sola vez por grupo, no por cada alumno escaneado.
  // Este ref recuerda { key, surveyResponseId, surveyResponse } de la última
  // ficha YA CONFIRMADA (enviada de verdad), y handleScan la reutiliza
  // mientras el siguiente alumno escaneado pertenezca a la MISMA salida
  // pedagógica (mismo id+source de studentStatus, ver
  // AccessControlController::resolveStudentStatus).
  const pedagogicalExitCacheRef = useRef(null);
  // Mismo propósito que pedagogicalExitCacheRef, para la ficha de Salida por
  // Enfermedad — evita volver a pedirla si se re-escanea al mismo alumno
  // dentro de la misma sesión.
  const sickExitCacheRef = useRef(null);
  const pendingScanRef = useRef(null);
  const loggingRef     = useRef(false);
  const [activePreview, setActivePreview] = useState(null);
  // "Ver ficha" de una respuesta YA ENVIADA de verdad al servidor (tiene id
  // real) — reusa el mismo modal con cabecera/logo y botón "Ver PDF" que ya
  // existe en el Perfil del alumno (ver FichasTab.js), en vez del preview
  // simple de solo lectura de arriba (ese sigue usándose para borradores
  // todavía no enviados, que no tienen id ni PDF generable).
  const [viewingFichaData, setViewingFichaData] = useState(null);
  // A cuál de las 3 fichas corresponde lo que se está viendo — necesario
  // para saber qué estado limpiar si el operador la elimina desde ahí (ver
  // handleFichaDeleted más abajo; ese modal trae su propia opción "Eliminar
  // formulario", reusada tal cual).
  const [viewingFichaKind, setViewingFichaKind] = useState(null); // 'pickup' | 'pedagogical' | 'sick'
  const { isOpen: isFichaViewOpen, onOpen: onOpenFichaView, onClose: onCloseFichaView } = useDisclosure();

  // Respaldo en sessionStorage de la ficha de Salida Pedagógica a medio
  // completar (ver PENDING_FICHA_* arriba) — clave por la salida (grupo), no
  // por alumno, porque la ficha es una sola para todo el curso.
  useEffect(() => {
    const key = pedagogicalExitKey(studentStatus);
    if (!key || !pedagogicalSurveyDraft) return;
    savePendingFicha(`pedagogical:${key}`, {
      draft: pedagogicalSurveyDraft,
      encargado: pedagogicalEncargado,
      authorizedBy: pedagogicalAuthorizedBy,
    });
  }, [studentStatus, pedagogicalSurveyDraft, pedagogicalEncargado, pedagogicalAuthorizedBy]);

  // Mismo respaldo para Salida por Enfermedad.
  useEffect(() => {
    const key = sickExitKey(studentStatus);
    if (!key || !sickSurveyDraft) return;
    savePendingFicha(`sick:${key}`, {
      draft: sickSurveyDraft,
      encargado: sickEncargado,
      authorizedBy: sickAuthorizedBy,
    });
  }, [studentStatus, sickSurveyDraft, sickEncargado, sickAuthorizedBy]);

  // Mismo respaldo para Retiro Anticipado — clave por alumno (acá sí es 1 a 1).
  useEffect(() => {
    if (!student?.rut || !pickupSurveyDraft) return;
    savePendingFicha(`pickup:${student.rut}`, { draft: pickupSurveyDraft });
  }, [student, pickupSurveyDraft]);

  const { isOpen: isConfigOpen, onOpen: onOpenConfig, onClose: onCloseConfig } = useDisclosure();
  // Panel lateral de historial — SIEMPRE montado (ver HistoryPanel), a
  // diferencia del Drawer que reemplaza. Solo alterna entre expandido/
  // minimizado, con la preferencia persistida en localStorage.
  //
  // Default por tamaño de pantalla: en tablet/mobile (< lg) el panel de
  // 33.333% de ancho le robaría demasiado espacio a la cámara/lector — así
  // que arranca colapsado ahí, y expandido en desktop — PERO solo mientras
  // el operador no haya tocado el botón de colapsar/expandir a mano en este
  // dispositivo (loadHistoryPanelCollapsed() !== null); una vez que lo toca,
  // esa elección manda siempre, sin importar el tamaño de pantalla.
  // `?? true` (colapsado) como fallback antes de que Chakra mida el
  // viewport real, para no mostrar nunca un parpadeo de sidebar gigante en
  // mobile mientras se resuelve el breakpoint.
  const isMobileOrTablet = useBreakpointValue({ base: true, lg: false }) ?? true;
  const hasExplicitHistoryPrefRef = useRef(loadHistoryPanelCollapsed() !== null);
  const [historyCollapsed, setHistoryCollapsed] = useState(() => {
    const stored = loadHistoryPanelCollapsed();
    return stored !== null ? stored : true;
  });
  useEffect(() => {
    if (hasExplicitHistoryPrefRef.current) return;
    setHistoryCollapsed(isMobileOrTablet);
  }, [isMobileOrTablet]);
  const toggleHistoryCollapsed = useCallback(() => {
    hasExplicitHistoryPrefRef.current = true;
    setHistoryCollapsed(prev => {
      const next = !prev;
      saveHistoryPanelCollapsed(next);
      return next;
    });
  }, []);
  const { isOpen: isPickupSurveyOpen, onOpen: onOpenPickupSurvey, onClose: onClosePickupSurvey } = useDisclosure();
  const { isOpen: isPedagogicalSurveyOpen, onOpen: onOpenPedagogicalSurvey, onClose: onClosePedagogicalSurvey } = useDisclosure();
  const { isOpen: isSickSurveyOpen, onOpen: onOpenSickSurvey, onClose: onCloseSickSurvey } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onOpenPreview, onClose: onClosePreview } = useDisclosure();
  const toast = useToast();

  const { cacheSize, lastSyncDate, loading: cacheLoading, error: cacheError, lookupByToken, refreshCache } = useStudentsCache();
  const { pendingCount, syncing, enqueue, syncPending } = useOfflineQueue();

  useEffect(() => {
    const savedId = loadSavedScannerId(inputMode);
    if (!savedId) { setLoadingConfig(false); return; }
    listScanners()
      .then(res => {
        const all = res.data ?? [];
        const found = all.find(s => s.scanner_id === savedId && s.active);
        if (!found) return;
        setRemembered(true);
        if (found.mode === 'info_only') {
          // El lector recordado es de Consulta — "recordar" ahora funciona
          // igual para las 3 categorías (ver handleToggleRemember), así que
          // esta pantalla puede arrancar directo en Consulta. El switch
          // Entrada/Salida necesita igual algo que mostrar/restaurar
          // cuando la consulta termine — se resuelve un lector de Entrada
          // (o Salida si no hay) como ese fallback, igual que
          // handleToggleConsulta arma consultaPrevConfigRef en caliente.
          const fallback = all.find(s => s.mode === 'access_log' && s.active)
            ?? all.find(s => s.mode === 'exit_log' && s.active)
            ?? null;
          lastUsedByModeRef.current.info_only = found.scanner_id;
          consultaPrevConfigRef.current = fallback;
          setConfig(found);
          setIsConsultaActive(true);
        } else {
          setConfig(found);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingConfig(false));
  }, [inputMode]);

  useEffect(() => {
    const on  = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // El lector puede quedar abierto horas en la puerta — si un admin edita su
  // horario/modo/ubicación en ScannerAdmin mientras tanto, este refresco en
  // segundo plano lo aplica sin necesitar recargar la página. El estado del
  // alumno (suspensión, licencia, salida pedagógica) ya se consulta en vivo
  // en cada escaneo vía validate(), así que no necesita este mecanismo.
  const scannerIdRef = useRef(null);
  scannerIdRef.current = config?.scanner_id ?? null;

  // Trae TODOS los lectores activos (no solo el de `config`) — alimenta
  // allScanners, que el switch Entrada/Salida y el botón de Consulta usan
  // para saber sin abrir ningún modal si hay 0/1/2+ lectores de cada tipo.
  // Se dispara al montar y luego junto con el refresco periódico de abajo,
  // así ambos quedan sincronizados con lo que se edite en ScannerAdmin.
  const refreshAllScanners = useCallback(() => {
    listScanners()
      .then(res => setAllScanners((res.data ?? []).filter(s => s.active)))
      .catch(() => {});
  }, []);
  useEffect(() => { refreshAllScanners(); }, [refreshAllScanners]);

  useEffect(() => {
    if (!isOnline) return undefined;
    const CONFIG_REFRESH_MS = 2 * 60 * 1000;
    const id = setInterval(() => {
      refreshAllScanners();
      if (!scannerIdRef.current) return;
      listScanners()
        .then(res => {
          const found = (res.data ?? []).find(s => s.scanner_id === scannerIdRef.current);
          if (found) setConfig(found);
        })
        .catch(() => {});
    }, CONFIG_REFRESH_MS);
    return () => clearInterval(id);
  }, [isOnline]);

  const goToScanning = useCallback(() => {
    setPhase('scanning');
    setStudent(null);
    setStudentStatus(null);
    setPedagogicalExitActive(false);
    setIsEarlyExit(false);
    setScanError(null);
    setIsOfflineResult(false);
    setPickupSurveyDraft(null);
    setPickupSurveyPreview(null);
    setPedagogicalSurveyDraft(null);
    setPedagogicalSurveyResponseId(null);
    setPedagogicalSurveyPreview(null);
    setPedagogicalEncargado(null);
    setPedagogicalAuthorizedBy(null);
    setSickSurveyDraft(null);
    setSickSurveyResponseId(null);
    setSickSurveyPreview(null);
    setSickEncargado(null);
    setSickAuthorizedBy(null);
    setExitSummary(null);
    pendingScanRef.current = null;
    loggingRef.current = false;
    // Consulta (botón redondo de búsqueda) es de un solo uso: escanear/
    // buscar, ver la tarjeta de info, "Cerrar" — al volver a escanear se
    // restaura automáticamente el lector de Entrada/Salida que estaba
    // activo antes de tocar el botón, sin que el operador tenga que
    // acordarse de "salir" de Consulta a mano.
    if (consultaPrevConfigRef.current) {
      const restored = consultaPrevConfigRef.current;
      setConfig(restored);
      consultaPrevConfigRef.current = null;
      setIsConsultaActive(false);
      // "Recordar" es un solo mecanismo para las 3 categorías (ver
      // handleToggleRemember) — al volver de Consulta, refleja si el
      // lector de Entrada/Salida restaurado es en verdad el recordado (y
      // no el de Consulta que se acaba de dejar).
      setRemembered(loadSavedScannerId(inputMode) === restored.scanner_id);
    }
  }, [inputMode]);

  // Cambia de dispositivo de lectura (cámara/USB/manual) desde dentro de la
  // pantalla. Solo se permite mientras se está escaneando — cambiar en medio
  // de un resultado/confirmación limpiaría `config` y podría desviar al
  // operador a la pantalla "Sin lector" con un escaneo a medio confirmar.
  // Siempre limpia `config`: el efecto de carga de config (ver arriba, ya
  // depende de [inputMode]) vuelve a resolver el lector recordado para el
  // nuevo modo, o deja la pantalla "Sin lector" existente si no hay ninguno.
  //
  // OJO: la URL se actualiza con history.replaceState() directo, NO con
  // react-router (history.push/replace de useHistory) — este componente está
  // montado por tres rutas distintas (Scanner/ScannerUsb/ScannerManual, cada
  // una con su propio <Route component={...}>), así que navegar vía
  // react-router haría que <Switch> matchee un componente DISTINTO y
  // remontara ScannerShell entero, perdiendo todo el estado en curso (fase,
  // historial, lector recordado). replaceState cambia solo lo que se ve en
  // la barra de direcciones — no dispara hashchange, así que HashRouter ni
  // se entera — pero deja la URL correcta para un refresh posterior.
  const handleSwitchInputMode = useCallback((nextMode) => {
    if (nextMode === inputMode || phase !== 'scanning') return;
    setInputMode(nextMode);
    setConfig(null);
    setRemembered(false);
    const path = SCAN_MODE_PATHS[nextMode];
    if (path) window.history.replaceState(null, '', path);
  }, [inputMode, phase]);

  const addToHistory = useCallback((entry) => {
    setHistory(prev => [entry, ...prev].slice(0, MAX_HISTORY));
  }, []);

  const openFichaView = useCallback((responseId, kind) => {
    setViewingFichaData(null);
    setViewingFichaKind(kind);
    onOpenFichaView();
    getSurveyResponse(responseId)
      .then(setViewingFichaData)
      .catch(() => setViewingFichaData({ error: 'No se pudieron cargar los datos de la ficha' }));
  }, [onOpenFichaView]);

  // El modal de "Ver ficha" (ModalSurveyResponse, reusado) trae su propia
  // opción "Eliminar formulario" — sin esto, borrarla ahí dejaba el estado
  // de este componente apuntando a un survey_response que ya no existe: la
  // sección "Responsable" seguía mostrando "Ficha completada ✓" sin forma de
  // volver a completarla salvo recargando la página entera. Limpia el
  // estado de la ficha correspondiente para que ese bloque vuelva a mostrar
  // el picker + botón "Completar ficha de responsable".
  const handleFichaDeleted = useCallback(() => {
    if (viewingFichaKind === 'pickup') {
      setPickupSurveyDraft(null);
      setPickupSurveyPreview(null);
      if (student?.rut) clearPendingFicha(`pickup:${student.rut}`);
    } else if (viewingFichaKind === 'pedagogical') {
      setPedagogicalSurveyDraft(null);
      setPedagogicalSurveyResponseId(null);
      setPedagogicalSurveyPreview(null);
      pedagogicalExitCacheRef.current = null;
      const key = pedagogicalExitKey(studentStatus);
      if (key) clearPendingFicha(`pedagogical:${key}`);
    } else if (viewingFichaKind === 'sick') {
      setSickSurveyDraft(null);
      setSickSurveyResponseId(null);
      setSickSurveyPreview(null);
      sickExitCacheRef.current = null;
      const key = sickExitKey(studentStatus);
      if (key) clearPendingFicha(`sick:${key}`);
    }
    setViewingFichaKind(null);
  }, [viewingFichaKind, student, studentStatus]);

  const handleScan = useCallback(async (token) => {
    if (phase !== 'scanning' || loggingRef.current) return;
    loggingRef.current = true;
    pendingScanRef.current = token;
    setPhase('validating');

    if (isOnline) {
      try {
        const res = await validate({ token, scanner_id: config.scanner_id });
        if (!res.success) {
          setScanError(res.message ?? 'QR inválido o revocado');
          setPhase('error');
          addToHistory({ type: 'error', timestamp: new Date(), token });
          loggingRef.current = false;
          return;
        }
        setStudent(res.data);
        // student_status comes from the validate endpoint (null until backend provides it)
        const nextStatus = res.data?.student_status ?? null;
        setStudentStatus(nextStatus);
        setPedagogicalExitActive(isPedagogicalExitActive(nextStatus));
        setIsEarlyExit(detectEarlyExit(config));
        setIsOfflineResult(false);

        const nextKey = pedagogicalExitKey(nextStatus);
        const cached  = pedagogicalExitCacheRef.current;
        const reuse   = nextKey && cached?.key === nextKey;
        // El backend ahora resuelve si YA existe una ficha confirmada para
        // esta salida (por related_type/related_id) — tiene prioridad sobre
        // el cache en memoria (pedagogicalExitCacheRef, que un refresh de
        // página borra) y sobre cualquier borrador pendiente en
        // sessionStorage. Sin esto, un refresh justo después de confirmar
        // la ficha del primer alumno de un grupo hacía que el siguiente
        // escaneo no supiera que ya existía, y el operador podía volver a
        // completarla — duplicándola.
        const existingResponse = nextStatus?.existing_survey_response ?? null;

        if (existingResponse) {
          setPedagogicalSurveyResponseId(existingResponse.id);
          setPedagogicalSurveyPreview(existingResponse);
          setPedagogicalSurveyDraft(null);
          setPedagogicalEncargado(null);
          setPedagogicalAuthorizedBy(null);
          if (nextKey) {
            pedagogicalExitCacheRef.current = { key: nextKey, surveyResponseId: existingResponse.id, surveyResponse: existingResponse };
            clearPendingFicha(`pedagogical:${nextKey}`);
          }
        } else {
          setPedagogicalSurveyResponseId(reuse ? cached.surveyResponseId : null);
          setPedagogicalSurveyPreview(reuse ? cached.surveyResponse : null);

          // Si no hay una ficha YA confirmada para reusar, busca si quedó una
          // A MEDIO COMPLETAR de antes de un refresh (ver savePendingFicha) —
          // así no obliga a rehacer todo el formulario. `current ?? ...` para
          // no pisar picks recién hechos en memoria (ej. justo después de
          // crear una salida ad-hoc, ver handleCreatePedagogicalExit).
          const pendingPedagogical = !reuse ? loadPendingFicha(`pedagogical:${nextKey}`) : null;
          setPedagogicalSurveyDraft(current => current ?? pendingPedagogical?.draft ?? null);
          setPedagogicalEncargado(current => current ?? pendingPedagogical?.encargado ?? null);
          setPedagogicalAuthorizedBy(current => current ?? pendingPedagogical?.authorizedBy ?? null);
        }

        // Mismo mecanismo que arriba, para la ficha de Salida por Enfermedad.
        const nextSickKey = sickExitKey(nextStatus);
        const cachedSick  = sickExitCacheRef.current;
        const reuseSick   = nextSickKey && cachedSick?.key === nextSickKey;
        const existingSickResponse = nextStatus?.type === 'sick_exit' ? (nextStatus?.existing_survey_response ?? null) : null;

        if (existingSickResponse) {
          setSickSurveyResponseId(existingSickResponse.id);
          setSickSurveyPreview(existingSickResponse);
          setSickSurveyDraft(null);
          setSickEncargado(null);
          setSickAuthorizedBy(null);
          if (nextSickKey) {
            sickExitCacheRef.current = { key: nextSickKey, surveyResponseId: existingSickResponse.id, surveyResponse: existingSickResponse };
            clearPendingFicha(`sick:${nextSickKey}`);
          }
        } else {
          setSickSurveyResponseId(reuseSick ? cachedSick.surveyResponseId : null);
          setSickSurveyPreview(reuseSick ? cachedSick.surveyResponse : null);
          const pendingSick = !reuseSick ? loadPendingFicha(`sick:${nextSickKey}`) : null;
          setSickSurveyDraft(current => current ?? pendingSick?.draft ?? null);
          setSickEncargado(current => current ?? pendingSick?.encargado ?? null);
          setSickAuthorizedBy(current => current ?? pendingSick?.authorizedBy ?? null);
        }

        const pendingPickup = loadPendingFicha(`pickup:${res.data.rut}`);
        setPickupSurveyDraft(current => current ?? pendingPickup?.draft ?? null);

        setPhase('preview');
        return;
      } catch (e) {
        const isNetworkErr = !e.response;
        if (!isNetworkErr) {
          setScanError(e?.response?.data?.message ?? 'Error al validar');
          setPhase('error');
          addToHistory({ type: 'error', timestamp: new Date(), token });
          loggingRef.current = false;
          return;
        }
      }
    }

    const cached = await lookupByToken(token);
    if (!cached) {
      setScanError('Alumno no encontrado en cache local. Reconecta para actualizar los datos.');
      setPhase('error');
      addToHistory({ type: 'error', timestamp: new Date(), token });
      loggingRef.current = false;
      return;
    }

    setStudent({
      rut:        cached.rut,
      wp_user_id: cached.wp_user_id,
      first_name: cached.nombre?.split(' ')[0] ?? '',
      last_name:  cached.nombre?.split(' ').slice(1).join(' ') ?? '',
      avatar_url: cached.foto_url,
      email:      cached.email,
      meta_data:  cached.meta_data ?? [],
    });
    // Status not available in offline cache — will re-check on sync.
    // El operador ve un aviso explícito ("sin verificar") en StudentInfoCard
    // para no asumir que la ausencia de estado significa "todo normal".
    setStudentStatus(null);
    setIsEarlyExit(detectEarlyExit(config));
    setIsOfflineResult(true);
    const pendingPickup = loadPendingFicha(`pickup:${cached.rut}`);
    setPickupSurveyDraft(current => current ?? pendingPickup?.draft ?? null);
    setPhase('preview');
  }, [phase, isOnline, config, lookupByToken, addToHistory]);

  // summaryPayload (opcional): cuando se acaba de firmar una ficha NUEVA
  // (Retiro/Salida Pedagógica/Salida por Enfermedad), en vez de volver
  // directo a escanear se muestra una pantalla de resumen (ficha + contexto
  // del alumno/curso) — el operador la cierra con "Escanear siguiente".
  // Cuando la ficha se reusa (ej. siguiente alumno del mismo curso) no se
  // pasa nada acá y el comportamiento es el de siempre.
  const _doLog = useCallback(async (logData, historyExtra = {}, summaryPayload = null) => {
    // Único punto por el que pasan las 4 formas de confirmar (Entrada/
    // Salida directa, Salida Pedagógica, Salida por Enfermedad, Salida
    // Anticipada) — manual (botón) o automática (ver AUTO_CONFIRM_DELAY_MS
    // en el useEffect de "Auto"), así que el beep queda cubierto para
    // ambas sin duplicar la llamada en cada handler.
    playConfirmSound();
    // El resumen se muestra DE INMEDIATO, antes de esperar el registro de
    // entrada/salida (logAccess) — la ficha (lo que importa mostrar acá) ya
    // se guardó de verdad antes de llegar a esta función. Antes esto
    // esperaba a que logAccess/enqueue terminaran para recién ahí mostrar
    // el resumen: si esa llamada era lenta (o colgaba), el operador se
    // quedaba viendo "Registrando…" sin fin y nunca llegaba a verlo.
    if (summaryPayload) {
      setExitSummary(summaryPayload);
      setPhase('summary');
    }

    if (isOfflineResult || !isOnline) {
      await enqueue(logData);
    } else {
      try {
        await logAccess(logData);
      } catch (e) {
        // axios: sin e.response = la petición nunca llegó a obtener
        // respuesta del servidor (sin conexión real, DNS, timeout de red) —
        // con e.response, el servidor SÍ respondió (con un error) mientras
        // había internet. Antes esto se ignoraba acá y `is_offline` quedaba
        // SIEMPRE en true sin importar la causa real — cualquier error del
        // servidor (ej. el bug de "survey_responses" ya corregido) terminaba
        // marcado como "capturado sin conexión" en el Historial de Accesos,
        // aunque el dispositivo nunca hubiera perdido internet.
        const isNetworkFailure = !e?.response;
        await enqueue({ ...logData, is_offline: isNetworkFailure });
        if (isNetworkFailure) {
          toast({
            title: 'Sin conexión — registro guardado para sincronizar',
            status: 'warning', duration: 3000, position: 'bottom',
          });
        } else {
          toast({
            title: 'El servidor rechazó el registro de acceso',
            description: (e.response?.data?.message ?? (e.response?.data?.errors ? JSON.stringify(e.response.data.errors) : `Error ${e.response.status}`))
              + (summaryPayload ? ' — la ficha ya se guardó, solo falta este registro.' : ''),
            status: 'error', duration: 6000, position: 'bottom',
          });
        }
      }
    }
    addToHistory({
      type:      'success',
      timestamp: new Date(),
      name:      `${student.first_name ?? ''} ${student.last_name ?? ''}`.trim(),
      rut:       student.rut,
      mode:      config.mode,
      offline:   isOfflineResult || !isOnline,
      statusType: studentStatus?.type ?? null,
      pedagogical: studentStatus?.type === 'pedagogical_exit',
      ...historyExtra,
    });
    if (!summaryPayload) {
      goToScanning();
    }
  }, [student, config, isOnline, isOfflineResult, studentStatus, enqueue, addToHistory, goToScanning, toast]);

  const handleConfirm = useCallback(async () => {
    if (!student || !config) return;
    setPhase('confirming');
    await _doLog({
      rut:            student.rut,
      wp_user_id:     student.wp_user_id,
      scanner_id:     config.scanner_id,
      access_type:    modeToAccessType[config.mode] ?? 'entry',
      location:       config.location || null,
      scanned_at:     new Date().toISOString(),
      is_offline:     isOfflineResult,
      status_at_scan: studentStatus?.type ?? null,
    });
  }, [student, config, isOfflineResult, studentStatus, _doLog]);

  // Confirma una salida pedagógica YA programada/activa (tarjeta "SALIDA
  // PEDAGÓGICA"), una vez completada la ficha de responsable — a diferencia
  // de handleCreatePedagogicalExit, esto no crea nada nuevo, solo registra la
  // salida de ESTE alumno con la ficha ya llena. Si la ficha todavía es un
  // borrador (primer alumno del grupo), recién ACÁ se envía de verdad al
  // servidor — si ya se reusó de otro alumno del mismo grupo, el id real ya
  // existe (pedagogicalSurveyResponseId) y no hay nada que enviar de nuevo.
  const handleConfirmPedagogicalActive = useCallback(async () => {
    if (!student || !config) return;
    if (!pedagogicalSurveyResponseId && !pedagogicalSurveyDraft) return;
    setPhase('confirming');

    let responseId = pedagogicalSurveyResponseId;
    // Se arma solo cuando se firma una ficha NUEVA en esta confirmación (no
    // al reusar la de otro alumno del mismo curso) — dispara la pantalla de
    // resumen en _doLog. Ver Fase 3 del plan.
    let summaryPayload = null;
    if (!responseId) {
      // Encargado y quien-permite se envían como DOS survey_response
      // independientes (mismas respuestas del formulario, distinto
      // customer/username cada uno) en vez de uno solo atribuido al alumno
      // escaneado — survey_response solo soporta un respondent_id, así que
      // en vez de migrar el schema se manda el mismo payload dos veces con
      // la identidad de cada persona real elegida en StudentInfoCard.
      const permite = pedagogicalAuthorizedBy ?? currentStaffUser;
      if (!pedagogicalEncargado || !permite) {
        setPhase('preview');
        toast({
          title: 'Falta elegir encargado o quien autoriza',
          description: 'Selecciona ambas personas antes de completar la ficha de responsable.',
          status: 'warning', duration: 4000,
        });
        return;
      }
      const buildCustomer = (person) => ({
        id: person.id,
        username: person.username,
        first_name: person.first_name,
        last_name: person.last_name,
        email: person.email,
      });
      // Referencia trazable hacia el registro que originó esta ficha
      // (pedagogical_exits o student_statuses, según corresponda) — mismo
      // par source/id que ya usa pedagogicalExitKey() para el cache de
      // reuso. Sin esto, un survey_response ya guardado no tenía forma de
      // saber a qué salida específica pertenecía.
      const relatedType = studentStatus?.source ?? null;
      const relatedId   = studentStatus?.id ?? null;
      try {
        const [resultEncargado, resultPermite] = await Promise.all([
          submitSurveyResponse(
            pedagogicalSurveyDraft.site,
            pedagogicalSurveyDraft.surveySlug,
            // send_email: false — el backend envía el correo por defecto si el
            // campo viene ausente (SurveyController@webhook: `send_email !==
            // false`), y el borrador armado por ModalAddSurveyResponse en modo
            // deferSubmit no lo incluye. Acá se completa la ficha como parte
            // del flujo de salida del escáner, no como un envío de encuesta al
            // apoderado — no corresponde notificación por email.
            { ...pedagogicalSurveyDraft.payload, customer: buildCustomer(pedagogicalEncargado), username: pedagogicalEncargado.username, send_email: false, related_type: relatedType, related_id: relatedId },
          ),
          submitSurveyResponse(
            pedagogicalSurveyDraft.site,
            pedagogicalSurveyDraft.surveySlug,
            { ...pedagogicalSurveyDraft.payload, customer: buildCustomer(permite), username: permite.username, send_email: false, related_type: relatedType, related_id: relatedId },
          ),
        ]);
        // La copia de "encargado" es la principal: su id es la que se cachea
        // para reusar en el resto del grupo y la que queda en
        // access_logs.survey_response_id (columna única, no hay espacio para
        // una segunda referencia sin migrar). La copia de "permite" queda
        // como registro independiente en el sistema de encuestas, bajo su
        // propio nombre — no se referencia desde acá, pero sí se guarda en el
        // cache por si se necesita más adelante.
        responseId = resultEncargado?.survey_response?.id ?? null;
        if (!responseId) throw new Error('El servidor no devolvió el id de la ficha guardada');
        setPedagogicalSurveyPreview(resultEncargado.survey_response);
        const key = pedagogicalExitKey(studentStatus);
        if (key) {
          pedagogicalExitCacheRef.current = {
            key,
            surveyResponseId: responseId,
            surveyResponse: resultEncargado.survey_response,
            permiteSurveyResponseId: resultPermite?.survey_response?.id ?? null,
          };
          // Ya se envió de verdad — el respaldo de "a medio completar" ya no
          // sirve (y si quedara, un refresh futuro intentaría reusarlo).
          clearPendingFicha(`pedagogical:${key}`);
        }
        const rawGroup = student.meta_data?.find(m => m.key === 'group_name')?.value ?? null;
        const courseLabel = rawGroup ? codeToDisplayLabel(normalizeCourseName(rawGroup)) : null;
        summaryPayload = {
          kind: 'pedagogical',
          student,
          courseLabel,
          fichaItem: resultEncargado.survey_response,
          // La ficha es del CURSO, no de este alumno en particular (es solo
          // quien la disparó al ser el primero en salir) — mostrar su
          // nombre acá (como hace FichaPreviewPanel con `customer`) daría a
          // entender que la ficha le pertenece a él. Mismo criterio que ya
          // usa PedagogicalExitsTab.js en Admin.
          customer: { first_name: courseLabel ?? 'Curso', last_name: '', username: '' },
        };
      } catch (e) {
        setPhase('preview');
        toast({
          title: 'No se pudo guardar la ficha de responsable',
          description: e?.response?.data?.message ?? e.message,
          status: 'error', duration: 4000,
        });
        return;
      }
    }

    await _doLog({
      rut:                 student.rut,
      wp_user_id:          student.wp_user_id,
      scanner_id:          config.scanner_id,
      access_type:         modeToAccessType[config.mode] ?? 'entry',
      location:            config.location || null,
      scanned_at:          new Date().toISOString(),
      is_offline:          isOfflineResult,
      exit_reason:         'pedagogical',
      survey_response_id:  responseId,
      status_at_scan:      studentStatus?.type ?? null,
    }, {}, summaryPayload);
  }, [student, config, isOfflineResult, studentStatus, pedagogicalSurveyResponseId, pedagogicalSurveyDraft, pedagogicalEncargado, pedagogicalAuthorizedBy, currentStaffUser, _doLog, toast]);

  // Confirma una salida por enfermedad YA programada/activa (tarjeta "SALIDA
  // POR ENFERMEDAD"), una vez completada la ficha de responsable.
  //
  // A diferencia de handleConfirmPedagogicalActive, acá NO se sobreescribe
  // customer/username al enviar — se deja el que ya trae el borrador por
  // defecto (el ALUMNO escaneado, ver customer={{ username: student.rut,
  // ... }} en el <ModalAddSurveyResponse directAccess="salidas-enfermedad">
  // más abajo), para que la ficha quede asociada a él y aparezca en su
  // Perfil > Fichas (que busca por respondent.username === alumno.rut, ver
  // getRespondentSurveys en Profile/index.js). Pedagógica sí sobreescribe a
  // propósito porque cubre un curso completo — ahí no hay un único alumno al
  // cual asociar la ficha, así que usa a encargado/autorizado como
  // respondent. Sick exit es individual, así que no hace falta ese truco ni
  // la doble firma (una sola ficha por salida) — "Nombre del responsable" y
  // "Autorizado por" ya quedan capturados como respuestas dentro de la
  // misma ficha (auto-rellenadas al completarla, ver pedagogicalEncargado/
  // pedagogicalAuthorizedBy más abajo).
  const handleConfirmSickActive = useCallback(async () => {
    if (!student || !config) return;
    if (!sickSurveyResponseId && !sickSurveyDraft) return;
    setPhase('confirming');

    let responseId = sickSurveyResponseId;
    let summaryPayload = null;
    if (!responseId) {
      const permite = sickAuthorizedBy ?? currentStaffUser;
      if (!sickEncargado || !permite) {
        setPhase('preview');
        toast({
          title: 'Falta elegir responsable o quien autoriza',
          description: 'Selecciona ambas personas antes de completar la ficha de responsable.',
          status: 'warning', duration: 4000,
        });
        return;
      }
      const relatedType = studentStatus?.source ?? null;
      const relatedId   = studentStatus?.id ?? null;
      try {
        const result = await submitSurveyResponse(
          sickSurveyDraft.site,
          sickSurveyDraft.surveySlug,
          { ...sickSurveyDraft.payload, send_email: false, related_type: relatedType, related_id: relatedId },
        );
        responseId = result?.survey_response?.id ?? null;
        if (!responseId) throw new Error('El servidor no devolvió el id de la ficha guardada');
        setSickSurveyPreview(result.survey_response);
        const key = sickExitKey(studentStatus);
        if (key) {
          sickExitCacheRef.current = { key, surveyResponseId: responseId, surveyResponse: result.survey_response };
          clearPendingFicha(`sick:${key}`);
        }
        summaryPayload = {
          kind: 'sick',
          student,
          fichaItem: result.survey_response,
          customer: {
            username:   student.rut,
            first_name: student.first_name,
            last_name:  student.last_name,
            email:      student.email,
            meta_data:  student.meta_data,
            id:         student.wp_user_id,
          },
        };
      } catch (e) {
        setPhase('preview');
        toast({
          title: 'No se pudo guardar la ficha de responsable',
          description: e?.response?.data?.message ?? e.message,
          status: 'error', duration: 4000,
        });
        return;
      }
    }

    await _doLog({
      rut:                 student.rut,
      wp_user_id:          student.wp_user_id,
      scanner_id:          config.scanner_id,
      access_type:         modeToAccessType[config.mode] ?? 'entry',
      location:            config.location || null,
      scanned_at:          new Date().toISOString(),
      is_offline:          isOfflineResult,
      exit_reason:         'sick',
      survey_response_id:  responseId,
      status_at_scan:      studentStatus?.type ?? null,
    }, {}, summaryPayload);
  }, [student, config, isOfflineResult, studentStatus, sickSurveyResponseId, sickSurveyDraft, sickEncargado, sickAuthorizedBy, currentStaffUser, _doLog, toast]);

  // Called by StudentInfoCard when reason is selected for early exits — para
  // "Retiro" (pickup), la ficha es siempre un borrador nuevo (no hay reuso
  // por grupo como en salida pedagógica), así que se envía acá mismo, justo
  // antes de registrar la salida.
  const handleConfirmWithReason = useCallback(async ({ reason, guardianPresent, notes }) => {
    if (!student || !config) return;
    if (reason === 'pickup' && !pickupSurveyDraft) return;
    setPhase('confirming');

    let surveyResponseId = null;
    let summaryPayload = null;
    if (reason === 'pickup') {
      try {
        const result = await submitSurveyResponse(
          pickupSurveyDraft.site,
          pickupSurveyDraft.surveySlug,
          // send_email: false — mismo motivo que en handleConfirmPedagogicalActive.
          { ...pickupSurveyDraft.payload, send_email: false },
        );
        surveyResponseId = result?.survey_response?.id ?? null;
        if (!surveyResponseId) throw new Error('El servidor no devolvió el id de la ficha guardada');
        setPickupSurveyPreview(result.survey_response);
        clearPendingFicha(`pickup:${student.rut}`);
        summaryPayload = {
          kind: 'pickup',
          student,
          fichaItem: result.survey_response,
          customer: {
            username:   student.rut,
            first_name: student.first_name,
            last_name:  student.last_name,
            email:      student.email,
            meta_data:  student.meta_data,
            id:         student.wp_user_id,
          },
        };
      } catch (e) {
        setPhase('preview');
        toast({
          title: 'No se pudo guardar la ficha de retiro',
          description: e?.response?.data?.message ?? e.message,
          status: 'error', duration: 4000,
        });
        return;
      }
    }

    await _doLog(
      {
        rut:                 student.rut,
        wp_user_id:          student.wp_user_id,
        scanner_id:          config.scanner_id,
        access_type:         'exit',
        location:            config.location || null,
        scanned_at:          new Date().toISOString(),
        is_offline:          isOfflineResult,
        early_exit:          true,
        exit_reason:         reason,
        guardian_present:    guardianPresent,
        exit_notes:          notes || null,
        survey_response_id:  reason === 'pickup' ? surveyResponseId : null,
        status_at_scan:      studentStatus?.type ?? null,
      },
      { earlyExit: true, reason },
      summaryPayload,
    );
  }, [student, config, isOfflineResult, studentStatus, pickupSurveyDraft, _doLog, toast]);

  // Atajo desde "Salida anticipada" → motivo "Salida pedagógica": el curso del
  // alumno no tenía una salida pedagógica programada, así que se crea al
  // vuelo en la MISMA tabla/listado que ya usa Admin > Accesos QR > Salidas
  // Pedagógicas (por curso, no un registro individual aparte) — así queda
  // visible ahí y autoriza a todo el curso, no solo a este alumno. Se pide
  // re-escanear para que el flujo normal de resolveStudentStatus() la
  // reconozca — mismo mecanismo que ya usa la tarjeta "SALIDA PEDAGÓGICA".
  const handleCreatePedagogicalExit = useCallback(async ({ title, authorizedBy, responsibleName, endsAt, encargadoPerson, permitePerson }) => {
    if (!student || !endsAt) return;
    const rawGroup = student.meta_data?.find(m => m.key === 'group_name')?.value ?? null;
    if (!rawGroup) {
      toast({
        title: 'No se pudo determinar el curso del alumno',
        description: 'Falta el curso en su perfil — créala manualmente desde Admin > Salidas Pedagógicas.',
        status: 'error', duration: 5000,
      });
      return;
    }
    const pad = (n) => String(n).padStart(2, '0');
    const toNaiveLocal = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    const [h, m] = endsAt.split(':').map(Number);
    const endsDate = new Date();
    endsDate.setHours(h, m, 0, 0);

    try {
      const res = await upsertPedagogicalExit({
        title:            title || 'Salida pedagógica',
        group_names:      [rawGroup],
        authorized_by:    authorizedBy || null,
        responsible_name: responsibleName || null,
        starts_at:        toNaiveLocal(new Date()),
        ends_at:          toNaiveLocal(endsDate),
        active:           true,
      });
      toast({
        title: 'Salida pedagógica registrada',
        description: 'Completa la ficha de responsable para confirmar la salida.',
        status: 'success', duration: 4000,
      });
      // Antes se pedía re-escanear para que resolveStudentStatus() (backend)
      // resolviera la tarjeta "SALIDA PEDAGÓGICA" — un paso extra
      // innecesario, ya se tiene al alumno en pantalla. upsertPedagogicalExit
      // ya devuelve el registro recién creado con todo lo necesario, así que
      // se arma acá mismo el mismo shape que resolveStudentStatus() (ver
      // AccessControlController.php, rama pedagogical_exit) para pasar
      // directo a la tarjeta activa, sin ir de nuevo al backend.
      const exit = res.data;
      const endsDateResolved = new Date(exit.ends_at);
      setStudentStatus({
        type:                      'pedagogical_exit',
        id:                        exit.id,
        source:                    'pedagogical_exit',
        title:                     exit.title,
        authorized_by:             exit.authorized_by,
        responsible_name:          exit.responsible_name,
        ends_at:                   `${pad(endsDateResolved.getHours())}:${pad(endsDateResolved.getMinutes())}`,
        existing_survey_response:  null,
      });
      setPedagogicalExitActive(true);
      // El operador recién eligió encargado/quien-permite para ESTA salida
      // — se dejan cargados para no pedirlos de nuevo en la ficha.
      setPedagogicalEncargado(encargadoPerson ?? null);
      setPedagogicalAuthorizedBy(permitePerson ?? null);
    } catch (e) {
      toast({
        title: 'Error al registrar la salida pedagógica',
        description: e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : e.message,
        status: 'error', duration: 4000,
      });
    }
  }, [student, toast]);

  // Atajo desde "Salida anticipada" → motivo "Enfermedad": a diferencia de
  // Salida Pedagógica, es individual (no depende del curso) y solo dura el
  // día de hoy — se crea como student_status tipo "sick_exit" (starts_at
  // 00:00 / ends_at 23:59 de hoy). Igual que handleCreatePedagogicalExit,
  // no hace falta re-escanear: upsertStudentStatus ya devuelve el registro
  // recién creado con todo lo necesario para armar la tarjeta activa acá
  // mismo (mismo shape que resolveStudentStatus(), rama sick_exit).
  const handleCreateSickExit = useCallback(async ({ title, authorizedBy, responsibleName }) => {
    if (!student) return;
    const pad = (n) => String(n).padStart(2, '0');
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

    try {
      const res = await upsertStudentStatus({
        wp_user_id:        student.wp_user_id,
        rut:                student.rut,
        type:               'sick_exit',
        reason:             title || 'Enfermedad',
        starts_at:          `${dateStr}T00:00:00`,
        ends_at:            `${dateStr}T23:59:59`,
        authorized_by:      authorizedBy || null,
        responsible_name:   responsibleName || null,
        active:             true,
      });
      toast({
        title: 'Salida por enfermedad registrada',
        description: 'Completa la ficha de responsable para confirmar la salida.',
        status: 'success', duration: 4000,
      });
      const status = res.data;
      setStudentStatus({
        type:                      'sick_exit',
        id:                        status.id,
        source:                    'student_status',
        title:                     status.reason ?? 'Enfermedad',
        authorized_by:             status.authorized_by,
        responsible_name:          status.responsible_name,
        ends_at:                   status.ends_at,
        existing_survey_response:  null,
      });
    } catch (e) {
      toast({
        title: 'Error al registrar la salida por enfermedad',
        description: e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : e.message,
        status: 'error', duration: 4000,
      });
    }
  }, [student, toast]);

  // Derivados — también los usa el JSX más abajo (props pedagogicalSurveyReady/
  // sickSurveyReady de StudentInfoCard) para no duplicar la misma condición.
  const pedagogicalSurveyReady = !!pedagogicalSurveyResponseId || !!pedagogicalSurveyDraft;
  const sickSurveyReady = !!sickSurveyResponseId || !!sickSurveyDraft;

  // Réplica de la prioridad de tarjetas de StudentInfoCard.js para decidir
  // qué botón "presionar" solo — o si no se debe tocar nada:
  // - 'confirm': hay un botón de confirmación directo, sin input extra.
  // - 'confirm-pedagogical'/'confirm-sick': salida pedagógica/por enfermedad
  //   ya activa CON la ficha de responsable ya lista (reusada de un alumno
  //   anterior del mismo curso, o de un re-escaneo) — antes esta rama caía
  //   en 'confirm' genérico y el auto-confirmar terminaba llamando
  //   handleConfirm() (el de Entrada/registro simple) en vez del handler
  //   correcto, o peor, confirmaba sin haber completado la ficha. Si la
  //   ficha NO está lista todavía, no hay nada que "presionar solo" — hace
  //   falta elegir responsable/autorizado y firmar, así que cae a 'manual'.
  // - 'manual': "Salida anticipada" exige elegir un motivo primero, y un
  //   alumno suspendido en Entrada queda bloqueado (no hay nada que
  //   confirmar) — en ambos casos requiere al operador. Consulta también
  //   cae acá siempre (ver más abajo): "Auto" no tiene sentido ahí — el
  //   resultado ahora es el tab Antecedentes completo (ver ConsultaResult),
  //   no una ficha chica para "despachar" sola; auto-cerrarla a los pocos
  //   segundos le cortaría la lectura al operador. Se cierra solo con
  //   "Cerrar" o el botón redondo de Consulta, nunca sola.
  const autoAdvanceAction = useCallback(() => {
    if (!config) return 'manual';
    if (config.mode === 'info_only') return 'manual';
    if (studentStatus?.type === 'suspended' && config.mode === 'access_log') return 'manual';
    if (studentStatus?.type === 'pedagogical_exit' && config.mode === 'exit_log' && pedagogicalExitActive) {
      return pedagogicalSurveyReady ? 'confirm-pedagogical' : 'manual';
    }
    if (studentStatus?.type === 'sick_exit' && config.mode === 'exit_log') {
      return sickSurveyReady ? 'confirm-sick' : 'manual';
    }
    if (studentStatus?.type === 'suspended' && config.mode === 'exit_log') return 'confirm';
    if (isEarlyExit && config.mode === 'exit_log') return 'manual';
    return 'confirm';
  }, [config, studentStatus, pedagogicalExitActive, pedagogicalSurveyReady, sickSurveyReady, isEarlyExit]);

  // Tiempo que "Auto" espera antes de confirmar solo, para dar lugar a leer
  // la tarjeta en pantalla y cancelar si no corresponde.
  const AUTO_CONFIRM_DELAY_MS = 7000;

  useEffect(() => {
    if (!autoConfirm || phase !== 'preview') { setAutoCountdown(null); return undefined; }
    const action = autoAdvanceAction();
    if (action === 'manual') { setAutoCountdown(null); return undefined; }

    const startedAt = Date.now();
    setAutoCountdown(Math.ceil(AUTO_CONFIRM_DELAY_MS / 1000));
    // Recalcula desde el tiempo real transcurrido (no un contador manual)
    // para que no se desincronice del setTimeout de abajo si el tab pierde
    // foco/el navegador throttlea los timers en segundo plano.
    const tick = setInterval(() => {
      const remainingMs = AUTO_CONFIRM_DELAY_MS - (Date.now() - startedAt);
      setAutoCountdown(Math.max(0, Math.ceil(remainingMs / 1000)));
    }, 250);

    const t = setTimeout(() => {
      if (action === 'confirm-pedagogical') handleConfirmPedagogicalActive();
      else if (action === 'confirm-sick') handleConfirmSickActive();
      else handleConfirm();
    }, AUTO_CONFIRM_DELAY_MS);

    return () => { clearTimeout(t); clearInterval(tick); setAutoCountdown(null); };
  }, [autoConfirm, phase, autoAdvanceAction, handleConfirm, handleConfirmPedagogicalActive, handleConfirmSickActive]);

  // "Auto" se auto-activa solo, una única vez por lector seleccionado
  // (scanner_id) — para Entrada, apenas se elige/carga ese lector; el ref
  // (no una condición sobre autoConfirm) es lo que evita que se vuelva a
  // forzar si el operador lo apaga después dentro de la misma sesión.
  const autoEntradaAppliedRef = useRef(null);
  useEffect(() => {
    if (!config || config.mode !== 'access_log') return;
    if (autoEntradaAppliedRef.current === config.scanner_id) return;
    autoEntradaAppliedRef.current = config.scanner_id;
    setAutoConfirm(true);
  }, [config, setAutoConfirm]);

  // Mismo criterio para Salida, pero acotado a salida PEDAGÓGICA activa —
  // enfermedad, retiro anticipado y suspensión siguen siendo manuales, ya
  // que autoAdvanceAction() de todos modos exige completar una ficha antes
  // de poder confirmarlos solos.
  const autoPedagogicalAppliedRef = useRef(null);
  useEffect(() => {
    if (!config || config.mode !== 'exit_log') return;
    if (studentStatus?.type !== 'pedagogical_exit' || !pedagogicalExitActive) return;
    if (autoPedagogicalAppliedRef.current === config.scanner_id) return;
    autoPedagogicalAppliedRef.current = config.scanner_id;
    setAutoConfirm(true);
  }, [config, studentStatus, pedagogicalExitActive, setAutoConfirm]);

  const handleSelectConfig = useCallback((scanner, remember) => {
    setConfig(scanner);
    setRemembered(remember);
    lastUsedByModeRef.current[scanner.mode] = scanner.scanner_id;
  }, []);

  // Recordar — un solo mecanismo para las 3 categorías (Entrada/Salida/
  // Consulta): guarda el `scanner_id` ACTIVO ahora mismo, sea cual sea.
  // Si se guarda estando en Consulta, la próxima carga de página arranca
  // directo ahí (ver el useEffect de carga inicial, que arma un fallback de
  // Entrada/Salida para consultaPrevConfigRef en ese caso).
  const handleToggleRemember = useCallback((e) => {
    const checked = e.target.checked;
    setRemembered(checked);
    if (!config) return;
    if (checked) saveScannerId(inputMode, config.scanner_id);
    else clearScannerId(inputMode);
  }, [inputMode, config]);

  // Decide qué hacer para dejar activo un lector de `mode` (access_log/
  // exit_log/info_only) sin abrir ningún modal cuando es posible:
  //  - 0 lectores activos de ese tipo  → 'empty' (el llamador avisa con un toast).
  //  - 1 lector                        → 'direct', se aplica al toque.
  //  - 2+ lectores, uno ya usado antes → 'direct' con ese mismo (lastUsedByModeRef).
  //  - 2+ lectores sin uso previo      → 'ambiguous', hace falta desempatar a mano.
  const resolveCategory = useCallback((mode) => {
    const bucket = allScanners.filter(s => s.mode === mode);
    if (bucket.length === 0) return { type: 'empty' };
    if (bucket.length === 1) return { type: 'direct', scanner: bucket[0] };
    const lastId = lastUsedByModeRef.current[mode];
    const last = lastId && bucket.find(s => s.scanner_id === lastId);
    if (last) return { type: 'direct', scanner: last };
    return { type: 'ambiguous', bucket };
  }, [allScanners]);

  // Aplica un lector de Entrada/Salida resuelto por el switch — a
  // diferencia de Consulta (ver handleToggleConsulta), esto SÍ puede quedar
  // recordado: si "Recordar este lector" ya estaba activo, el toggle sigue
  // guardando el lector nuevo como el recordado para este inputMode.
  const applyScanner = useCallback((scanner) => {
    setConfig(scanner);
    lastUsedByModeRef.current[scanner.mode] = scanner.scanner_id;
    if (remembered) saveScannerId(inputMode, scanner.scanner_id);
  }, [inputMode, remembered]);

  // Switch Entrada/Salida — alterna `config` entre el lector de Entrada y
  // el de Salida de este dispositivo. Deshabilitado fuera de "scanning" por
  // el mismo motivo que el selector de dispositivo de lectura (ver
  // handleSwitchInputMode): cambiar el lector activo a medio confirmar un
  // escaneo podría desviar al operador con un resultado a medio terminar.
  const handleToggleAccessMode = useCallback(() => {
    // isConsultaActive: mientras se espera un escaneo/búsqueda de Consulta,
    // `config` ya no es el lector de Entrada/Salida (ver handleToggleConsulta)
    // — tocar el switch en ese momento no debe pelear con esa restauración
    // pendiente en goToScanning(). El botón queda deshabilitado visualmente
    // por lo mismo (ver disabled/opacity más abajo en el JSX).
    if (phase !== 'scanning' || isConsultaActive) return;
    const target = config?.mode === 'exit_log' ? 'access_log' : 'exit_log';
    const result = resolveCategory(target);
    if (result.type === 'empty') {
      toast({ title: `No hay lector de ${ACCESS_MODE_LABEL[target]} configurado`, status: 'warning', duration: 3500, position: 'bottom' });
      return;
    }
    if (result.type === 'direct') { applyScanner(result.scanner); return; }
    setPendingPickerCategory(target);
  }, [phase, isConsultaActive, config, resolveCategory, applyScanner, toast]);

  // Botón redondo de Consulta (lupa) — deja `config` apuntando
  // temporalmente a un lector de tipo Consulta (info_only) para escanear o
  // buscar y ver esa info de solo lectura (StudentInfoCard ya tiene esa
  // vista lista, ver rama scanMode === 'info_only'), sin registrar ninguna
  // entrada/salida. El lector de Entrada/Salida real queda guardado en
  // consultaPrevConfigRef y se restaura solo en goToScanning() — tocar el
  // botón de nuevo ANTES de escanear cancela la consulta a mano.
  const handleToggleConsulta = useCallback(() => {
    if (isConsultaActive) {
      if (consultaPrevConfigRef.current) {
        const restored = consultaPrevConfigRef.current;
        setConfig(restored);
        setRemembered(loadSavedScannerId(inputMode) === restored.scanner_id);
      }
      consultaPrevConfigRef.current = null;
      setIsConsultaActive(false);
      return;
    }
    if (phase !== 'scanning') return;
    const result = resolveCategory('info_only');
    if (result.type === 'empty') {
      toast({ title: 'No hay lector de Consulta configurado', status: 'warning', duration: 3500, position: 'bottom' });
      return;
    }
    if (result.type === 'ambiguous') {
      setPendingPickerCategory('info_only');
      return;
    }
    consultaPrevConfigRef.current = config;
    lastUsedByModeRef.current.info_only = result.scanner.scanner_id;
    setConfig(result.scanner);
    setIsConsultaActive(true);
    // "Recordar" es un solo mecanismo para las 3 categorías (ver
    // handleToggleRemember) — refleja si ESTE lector de Consulta es el
    // recordado, para que el bookmark no arrastre el estado del lector de
    // Entrada/Salida que se acaba de dejar.
    setRemembered(loadSavedScannerId(inputMode) === result.scanner.scanner_id);
  }, [isConsultaActive, phase, config, resolveCategory, toast, inputMode]);

  // onSelect del <ScannerConfig forcedCategory> que desempata cuando hay 2+
  // lectores activos del mismo tipo (ver 'ambiguous' arriba) — Consulta
  // entra "a mano" (setIsConsultaActive) igual que handleToggleConsulta,
  // sincronizando "Recordar" con el lector elegido; Entrada/Salida reusa
  // applyScanner tal cual (mismo comportamiento "pegajoso" de siempre: si
  // ya estaba recordado, sigue guardando el que quede activo).
  const handlePendingPickerSelect = useCallback((scanner) => {
    if (pendingPickerCategory === 'info_only') {
      consultaPrevConfigRef.current = config;
      lastUsedByModeRef.current.info_only = scanner.scanner_id;
      setConfig(scanner);
      setIsConsultaActive(true);
      setRemembered(loadSavedScannerId(inputMode) === scanner.scanner_id);
    } else {
      applyScanner(scanner);
    }
    setPendingPickerCategory(null);
  }, [pendingPickerCategory, config, applyScanner, inputMode]);

  // historyIcon/EXIT_REASON_LABELS ahora viven en HistoryPanel.js — el
  // historial se muestra ahí (panel lateral fijo), no acá.

  // ── Bloqueo por rol ───────────────────────────────────────────────────────
  // Solo administrador e inspector (useCanOpenScanner). `currentStaffUser &&`
  // evita el flash de "sin permisos" mientras useUserAuth todavía está
  // cargando (currentStaffUser null ⇒ canOpenScanner también da false).
  if (currentStaffUser && !canOpenScanner) {
    return (
      <Box m="-30px" height="100vh" bg="cream.bg" display="flex" alignItems="center" justifyContent="center" px={4}>
        <Alert status="error" borderRadius="16px" maxW="440px">
          <AlertIcon />
          <AlertDescription>No tienes permisos para ver esta sección.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  // ── Carga inicial ─────────────────────────────────────────────────────────
  if (loadingConfig) {
    return (
      <Box m="-30px" height="100vh" bg="cream.bg" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="coral.500" />
      </Box>
    );
  }

  // ── Sin lector ────────────────────────────────────────────────────────────
  if (!config) {
    return (
      <Box mt={`-${PANEL_PT}`} mx="-30px" mb="-30px" pt={NAVBAR_OFFSET} height="100vh" bg="cream.bg" px={{ base: 3, md: 6 }}
        display="flex" alignItems="center" justifyContent="center" fontFamily="jakarta">
        <VStack spacing={5} align="stretch" w="100%" maxW="440px">
          <Box bg="white" borderRadius="22px" border="1px solid" borderColor="cream.line"
            boxShadow="0 2px 12px -4px rgba(35,30,26,.08)" p={{ base: 5, md: 8 }}>
            <VStack spacing={4} align="center" textAlign="center">
              <Flex w="64px" h="64px" borderRadius="full" bg="coral.soft" align="center" justify="center">
                <Icon as={FiCpu} boxSize={8} color="coral.600" />
              </Flex>
              <VStack spacing={1}>
                <Text fontSize="17px" fontWeight="800" color="ink.900">Selecciona un lector</Text>
                <Text fontSize="13.5px" color="ink.400" fontWeight="500">
                  Este dispositivo no tiene un lector asignado. Elige cuál es esta puerta o ubicación.
                </Text>
              </VStack>
              <Button
                w="full" h="44px" borderRadius="12px" leftIcon={<FiSettings />}
                bg="coral.500" color="#fff" fontWeight="700" _hover={{ bg: 'coral.600' }}
                onClick={onOpenConfig}
              >
                Elegir lector
              </Button>
            </VStack>
          </Box>
        </VStack>
        <ScannerConfig isOpen={isConfigOpen} onClose={onCloseConfig} currentScannerId={null}
          onSelect={handleSelectConfig} inputMode={inputMode} initialRemember={false} />
      </Box>
    );
  }

  const showingResult = phase === 'preview' || phase === 'confirming' || phase === 'error';

  // Banner de estado del cache (prioridad: error > cargando > vacío offline)
  let cacheBanner = null;
  if (cacheError) {
    cacheBanner = (
      <HStack px={3} py={2} bg="#DC2626" borderRadius="10px" spacing={2} flexWrap="wrap" boxShadow="md">
        <Icon as={FiXCircle} color="#fff" boxSize={4} flexShrink={0} />
        <Text fontSize="13.5px" color="#fff" flex={1} fontWeight="600">No se pudo cargar el cache offline.</Text>
        <Button size="xs" bg="rgba(255,255,255,.2)" color="#fff" _hover={{ bg: 'rgba(255,255,255,.3)' }} onClick={() => refreshCache(true)}>
          Reintentar
        </Button>
      </HStack>
    );
  } else if (cacheLoading) {
    cacheBanner = (
      <HStack px={3} py={2} bg="#2A66B4" borderRadius="10px" spacing={2} boxShadow="md">
        <Spinner size="xs" color="#fff" flexShrink={0} />
        <Text fontSize="13.5px" color="#fff" flex={1} fontWeight="600">Descargando datos para modo offline…</Text>
      </HStack>
    );
  } else if (cacheSize === 0 && !isOnline) {
    cacheBanner = (
      <HStack px={3} py={2} bg="#D97706" borderRadius="10px" spacing={2} boxShadow="md">
        <Icon as={FiWifiOff} color="#fff" boxSize={4} flexShrink={0} />
        <Text fontSize="13.5px" color="#fff" flex={1} fontWeight="600">Offline — cache vacío.</Text>
      </HStack>
    );
  }

  // Mientras Consulta está activa, `config` apunta al lector de Consulta
  // (para que handleScan/validate lo usen) — pero el switch Entrada/Salida
  // debe seguir mostrando el lector que va a quedar activo cuando se cierre
  // la consulta (consultaPrevConfigRef), no "Consulta", que no tiene lugar
  // en un switch de solo 2 posiciones.
  const accessConfig = isConsultaActive ? (consultaPrevConfigRef.current ?? config) : config;
  const isEntrada = accessConfig.mode !== 'exit_log';
  const accessMode = isEntrada ? 'access_log' : 'exit_log';
  const modeBadge = MODE_BADGE[accessMode];
  const modeGradient = MODE_GRADIENT[accessMode];
  // Mismo criterio que el guard de handleToggleAccessMode — deshabilita el
  // switch mientras se espera un escaneo/búsqueda de Consulta.
  const accessSwitchDisabled = phase !== 'scanning' || isConsultaActive;

  return (
    <Box
      mt={`-${PANEL_PT}`}
      mx="-30px"
      mb="-30px"
      height="100vh"
      sx={{ '@supports (height: 100dvh)': { height: '100dvh' } }}
      display="flex"
      flexDirection="row"
      overflow="hidden"
      position="relative"
      fontFamily="jakarta"
    >
    {/* Indicador de conexión — de verdad arriba del todo: fixed contra el
        viewport, superpuesto sobre el AdminNavbar global (h="60px", fijo,
        zIndex 5 — ver components/Navbars/AdminNavbar.js), no dentro de la
        barra propia de esta pantalla. AdminNavbar es compartido por TODA la
        app y no tiene ningún slot para inyectar contenido por ruta, así que
        en vez de tocarlo se superpone acá con position="fixed" (ninguno de
        los contenedores intermedios usa transform/filter, así que "fixed"
        sigue calculándose contra el viewport real) — el hueco vacío a la
        izquierda de esa barra (justo después del sidebar, antes de
        AdminNavbarLinks que está alineado a la derecha) es space libre.
        zIndex 20, por encima del zIndex 5 del navbar. Solo existe mientras
        ScannerShell está montado, así que no aparece en el resto de la app.
        OJO: top NO puede ser "50%" — para position="fixed" ese porcentaje
        se calcula contra el viewport COMPLETO (fixed no tiene como
        contenedor al navbar de 60px, sino el viewport entero), lo que dejaba
        el badge flotando a mitad de la pantalla en vez de centrado en la
        barra. Con h="30px" y el navbar midiendo 60px, top="15px" fijo es lo
        que efectivamente lo centra dentro de esos 60px. */}
    <HStack
      position="fixed" top="15px" left={{ base: '16px', xl: `${sidebarWidthPx + 16}px` }}
      zIndex={20} px="12px" h="30px" borderRadius="999px" spacing="6px"
      transition="left .18s ease"
      bg={isOnline ? 'mint.bg' : 'butter.bg'} boxShadow="0 2px 8px -2px rgba(35,30,26,.18)"
      title={isOnline ? 'Conectado' : 'Sin conexión'}
    >
      <Icon as={isOnline ? FiWifi : FiWifiOff}
        color={isOnline ? 'mint.ink' : 'butter.ink'} boxSize="13px" flexShrink={0} />
      <Text fontSize="12.5px" fontWeight="700" color={isOnline ? 'mint.ink' : 'butter.ink'} noOfLines={1}>
        {isOnline ? 'Online' : 'Sin señal'}
      </Text>
    </HStack>

    {/* Columna principal (barra superior + cámara/USB/manual) — el panel de
        historial (HistoryPanel, ver abajo) vive fuera de esta columna, fijo
        a la derecha de toda la pantalla. */}
    <Box flex="1" minW={0} display="flex" flexDirection="column" overflow="hidden" position="relative" bg={getShellBg(config.mode)}>
      {/* ── Barra superior ── */}
      <VStack
        align="stretch" spacing="8px"
        pt={NAVBAR_OFFSET} pb="16px" px={3}
        bg="white"
        flexShrink={0}
        borderBottom="1px solid" borderColor="cream.line"
        zIndex={5}
      >
        {/* Fila única con los controles restantes: columna angosta con 2
            chips (dispositivo de lectura con dropdown/auto-confirmar, ver
            más abajo) junto al box del lector (registro) — la etiqueta de
            tipo de registro se movió dentro del propio box del lector,
            achicada, para no competir por espacio en esta columna angosta. */}
        <Flex align="stretch" gap="10px" wrap="nowrap">
          <VStack w="34%" spacing="6px" align="stretch" flexShrink={0}>
            {pendingCount > 0 && (
              <HStack as="button" type="button" h="40px" px="10px" bg="butter.bg" borderRadius="12px" spacing="6px"
                cursor={isOnline ? 'pointer' : 'default'}
                title={isOnline ? 'Sincronizar pendientes' : `${pendingCount} pendiente(s) sin sincronizar`}
                onClick={isOnline ? syncPending : undefined}>
                <Icon as={syncing ? FiRefreshCw : FiUploadCloud} color="butter.ink" boxSize={5}
                  style={syncing ? { animation: 'spin 1s linear infinite' } : {}} />
                <Text fontSize="13.5px" fontWeight="700" color="butter.ink" flex={1} noOfLines={1}>
                  {pendingCount} pendiente{pendingCount === 1 ? '' : 's'}
                </Text>
              </HStack>
            )}

            {/* Una sola fila con 2 chips (dispositivo de lectura/
                auto-confirmar) — ícono junto al texto. "Conexión" se sacó de
                acá y ahora es un badge chico arriba del todo de la barra (ver
                más arriba). "Historial" también se sacó: el panel lateral
                (HistoryPanel) ya está siempre visible, con su propio botón
                de minimizar/expandir, así que un chip acá era redundante.
                "Recordar" también se sacó — ahora es el pequeño ícono de
                marcador sobre el tag de tipo (Entrada/Salida/Consulta/
                Validación) del selector de lector, a la derecha (ver más
                abajo). El dispositivo de lectura (cámara/USB/manual, antes
                una fila de 3 botones aparte) ahora es un chip más de esta
                fila, con el mismo look/proporción que su vecino "Auto". OJO:
                Chakra's <MenuButton> SIEMPRE envuelve su contenido en un
                <span> interno propio (flex:'1 1 auto', sin display:flex) —
                ver node_modules/@chakra-ui/menu, MenuButton — así que darle
                al ícono+texto un layout de fila (spacing/align) directo como
                children de MenuButton NO alcanza, ese span intermedio no es
                un contenedor flex y los deja desalineados/de tamaño
                distinto a "Auto". La solución: el ícono+texto van en un
                HStack con position="absolute" inset={0} que cubre TODO el
                MenuButton (position="relative") sin importar cómo mida ese
                span invisible — así siempre se ve idéntico al HStack de
                "Auto". flex={1} + align="stretch" en la fila para que ésta
                (y cada chip, h="100%") ocupe todo el alto disponible de la
                columna, igualando el alto real del selector de lector de al
                lado. */}
            <HStack spacing="6px" flex={1} align="stretch">
              {/* Dispositivo de lectura — cámara/USB/manual, cambiable sin
                  salir de esta pantalla. Deshabilitado fuera de "scanning"
                  (ver comentario en handleSwitchInputMode) y "Manual" se
                  apaga sin conexión, ya que necesita 2 llamadas en vivo
                  (buscar alumno + generar su token) sin equivalente offline. */}
              <Menu placement="bottom-start" isLazy>
                {(() => {
                  const current = SCAN_MODES.find(m => m.key === inputMode) ?? SCAN_MODES[0];
                  const menuDisabled = phase !== 'scanning';
                  return (
                    <>
                      <MenuButton
                        type="button"
                        position="relative"
                        flex={1} h="100%" borderRadius="10px"
                        bg="cream.bg2" _hover={{ bg: 'coral.soft' }}
                        opacity={menuDisabled ? 0.5 : 1} pointerEvents={menuDisabled ? 'none' : 'auto'}
                        title={menuDisabled ? 'Termina el escaneo actual primero' : 'Cambiar dispositivo de lectura'}
                      >
                        <HStack position="absolute" inset={0} spacing="6px" justify="center" align="center" px="6px">
                          <Icon as={current.icon} boxSize={5} color="ink.700" flexShrink={0} />
                          <Text fontSize="13px" fontWeight="700" color="ink.700" noOfLines={1}>{current.label}</Text>
                        </HStack>
                      </MenuButton>
                      <MenuList fontFamily="jakarta" minW="200px" zIndex={9999}>
                        {SCAN_MODES.map(m => {
                          const isSel = inputMode === m.key;
                          const isDisabled = m.key === 'manual' && !isOnline;
                          return (
                            <MenuItem
                              key={m.key}
                              fontSize="14.5px" py="10px"
                              icon={<Icon as={m.icon} boxSize={5} color={isSel ? 'coral.600' : 'ink.700'} />}
                              onClick={() => handleSwitchInputMode(m.key)}
                              isDisabled={isDisabled}
                              bg={isSel ? 'coral.tint' : 'transparent'}
                              fontWeight={isSel ? '700' : '500'}
                              color={isSel ? 'coral.700' : 'ink.900'}
                              title={isDisabled ? 'La búsqueda manual necesita conexión' : undefined}
                            >
                              {m.label}{isSel ? ' · Activo' : ''}
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </>
                  );
                })()}
              </Menu>

              {/* Opción temporal: confirma solo apenas se valida al alumno, sin
                  esperar el botón "Confirmar" — no aplica a salidas anticipadas
                  (piden motivo) ni a entradas bloqueadas por suspensión.
                  key={pulse}: fuerza un remount del nodo en cada cambio real
                  de autoConfirm (manual o auto-activado, ver refs arriba) para
                  reiniciar la animación CSS — sin esto, reasignar el mismo
                  `animation-name` dos veces seguidas no la vuelve a reproducir
                  (el navegador no detecta cambio). Con pulse=0 (aún no cambió
                  nada) no se anima, para no jugar el efecto solo por cargar
                  la pantalla. */}
              <HStack
                key={autoConfirmState.pulse}
                as="button" type="button"
                onClick={() => setAutoConfirm(v => !v)}
                flex={1} h="100%" px="6px" borderRadius="10px" spacing="4px" justify="center"
                bg="cream.bg2"
                _hover={{ bg: 'coral.soft' }}
                title="Confirmar automáticamente al validar (opción temporal)"
                sx={autoConfirmState.pulse > 0 ? {
                  animation: `${autoConfirm ? 'autoConfirmOn' : 'autoConfirmOff'} 520ms ease-out`,
                  '@keyframes autoConfirmOn': {
                    '0%':   { transform: 'scale(1)',    boxShadow: '0 0 0 0 rgba(239,90,42,.5)' },
                    '30%':  { transform: 'scale(1.1)',  boxShadow: '0 0 0 7px rgba(239,90,42,0)' },
                    '100%': { transform: 'scale(1)',    boxShadow: '0 0 0 0 rgba(239,90,42,0)' },
                  },
                  '@keyframes autoConfirmOff': {
                    '0%':   { transform: 'scale(1)',    opacity: 1 },
                    '40%':  { transform: 'scale(0.92)', opacity: .55 },
                    '100%': { transform: 'scale(1)',    opacity: 1 },
                  },
                } : undefined}
              >
                <Icon as={FiZap} boxSize={5} color={autoConfirm ? 'coral.600' : 'ink.400'} flexShrink={0} />
                <Text fontSize="13px" fontWeight="700" color={autoConfirm ? 'coral.600' : 'ink.700'} noOfLines={1}>
                  Auto
                </Text>
              </HStack>
            </HStack>
          </VStack>

          {/* Columna restante: marcador "Recordar" + switch animado
              Entrada/Salida (inspirado en un toggle "Day mode/Night mode" —
              la perilla con el ícono se desliza de un extremo a otro y el
              fondo cambia de color según el modo activo, con el nombre del
              lector ahora DENTRO del propio switch, como segunda línea bajo
              la etiqueta ENTRADA/SALIDA) + botón redondo de Consulta al
              lado. Antes acá vivía un único botón que abría el picker
              completo de categorías (Entrada/Salida/Consulta); ahora el
              switch resuelve Entrada/Salida con un solo toque (ver
              handleToggleAccessMode — si hay un solo lector de ese tipo lo
              aplica directo, si hay 2+ recuerda el último usado en la
              sesión) y Consulta se separó en su propio botón porque es una
              acción puntual ("escanear o buscar para ver esa info", no algo
              que se deje "prendido"). */}
          <HStack flex={1} minW={0} spacing="10px" align="center">
            {/* Recordar — a la izquierda del switch (antes vivía debajo,
                junto al nombre del lector, que ahora se movió adentro del
                propio switch). */}
            <IconButton
              icon={<Icon as={FiBookmark} boxSize="14px" />}
              aria-label={isConsultaActive ? 'Recordar este lector de Consulta en este dispositivo' : 'Recordar este lector en este dispositivo'}
              title={isConsultaActive ? 'Recordar este lector de Consulta en este dispositivo' : 'Recordar este lector en este dispositivo'}
              borderRadius="full" flexShrink={0}
              h={CHIP_H} w={CHIP_H} minW={CHIP_H}
              bg={remembered ? 'coral.500' : 'cream.bg2'}
              color={remembered ? '#fff' : 'ink.400'}
              border="1.5px solid" borderColor={remembered ? 'coral.500' : 'cream.line2'}
              _hover={{ bg: remembered ? 'coral.600' : 'cream.line' }}
              onClick={() => handleToggleRemember({ target: { checked: !remembered } })}
            />

            <Box
              as="button" type="button"
              onClick={handleToggleAccessMode}
              disabled={accessSwitchDisabled}
              position="relative"
              flex={1} minW={0} h={SWITCH_H}
              borderRadius="999px"
              bg={modeGradient}
              border="2px solid" borderColor={modeBadge.fg}
              opacity={accessSwitchDisabled ? 0.55 : 1}
              cursor={accessSwitchDisabled ? 'not-allowed' : 'pointer'}
              transition="background .4s ease, border-color .4s ease, opacity .2s ease"
              overflow="hidden"
              title={isConsultaActive ? 'Termina la consulta primero' : phase !== 'scanning' ? 'Termina el escaneo actual primero' : `Cambiar a ${isEntrada ? 'Salida' : 'Entrada'}`}
              aria-label={`Modo actual: ${modeBadge.label}, lector ${accessConfig.name}. Tocar para cambiar a ${isEntrada ? 'Salida' : 'Entrada'}`}
            >
              {/* Etiqueta + nombre del lector — siempre del lado OPUESTO a
                  la perilla, igual que en el switch de referencia. El
                  padding del lado de la perilla deja espacio para no
                  superponerse con ella (SWITCH_KNOB_MARGIN + ancho de la
                  perilla + un respiro chico). */}
              <Flex position="absolute" inset={0} align="center"
                justify={isEntrada ? 'flex-start' : 'flex-end'}
                pl={isEntrada ? '18px' : '56px'} pr={isEntrada ? '56px' : '18px'}
                pointerEvents="none">
                <VStack align={isEntrada ? 'flex-start' : 'flex-end'} spacing="0px" minW={0}>
                  <Text fontSize="13px" fontWeight="800" color={modeBadge.fg} letterSpacing="0.03em" noOfLines={1}>
                    {modeBadge.label.toUpperCase()}
                  </Text>
                  <Text fontSize="10.5px" fontWeight="600" color={modeBadge.fg} opacity={0.75} noOfLines={1}>
                    {accessConfig.name}{accessConfig.location ? ` · ${accessConfig.location}` : ''}
                  </Text>
                </VStack>
              </Flex>
              {/* Perilla — se desliza a la izquierda (Salida) o derecha
                  (Entrada) con la misma transición del selector de
                  dispositivo/Auto de al lado (ver key={pulse} arriba).
                  top/left en SWITCH_KNOB_MARGIN (no 4px como antes): con
                  border-box (el default acá), el borde de 2px del switch
                  ya resta alto/ancho del área interna — dejar el margen en
                  4px hacía que la perilla, de 40px, terminara TOCANDO el
                  borde de abajo por dentro (44 de margen+alto contra 44 de
                  alto interno real), en vez de quedar centrada con aire
                  parejo arriba y abajo. */}
              <Flex
                position="absolute" top={SWITCH_KNOB_MARGIN}
                left={isEntrada ? `calc(100% - ${SWITCH_KNOB_SIZE} - ${SWITCH_KNOB_MARGIN})` : SWITCH_KNOB_MARGIN}
                transition="left .35s cubic-bezier(.4,0,.2,1)"
                w={SWITCH_KNOB_SIZE} h={SWITCH_KNOB_SIZE} borderRadius="full" bg="white"
                boxShadow="0 2px 6px -1px rgba(35,30,26,.35)"
                align="center" justify="center"
              >
                <Icon as={modeBadge.icon} boxSize="20px" color={modeBadge.fg} />
              </Flex>
            </Box>

            {/* Botón redondo de Consulta — escanea/busca para ver la
                info de solo lectura que da el lector de Consulta
                (StudentInfoCard ya tiene esa vista, rama info_only), sin
                registrar entrada ni salida. Relleno mientras está activo,
                para que sea obvio que el próximo escaneo es una consulta
                y no un registro real (ver también el aviso flotante sobre
                la cámara/lector más abajo). */}
            <IconButton
              icon={<Icon as={FiSearch} boxSize="20px" />}
              aria-label={isConsultaActive ? 'Cancelar consulta' : 'Consultar información'}
              title={isConsultaActive
                ? 'Cancelar consulta'
                : 'Escanear o buscar para consultar información — no registra entrada ni salida'}
              onClick={handleToggleConsulta}
              isDisabled={phase !== 'scanning'}
              isRound
              h={CHIP_H} w={CHIP_H} minW={CHIP_H} flexShrink={0}
              bg={isConsultaActive ? 'sky.ink' : 'sky.bg'}
              color={isConsultaActive ? '#fff' : 'sky.ink'}
              border="2px solid" borderColor="sky.ink"
              _hover={{ opacity: .85 }}
            />
          </HStack>
        </Flex>
      </VStack>

      {/* ── Cámara / lector USB / búsqueda manual (ocupa el resto de la pantalla) ── */}
      <Box flex="1" position="relative" minH={0}>
        {inputMode === 'usb'
          ? <USBScanner active={phase === 'scanning'} onScan={handleScan} mode={config.mode}
              isModalOpen={isConfigOpen || !!pendingPickerCategory || isConsultaActive} />
          : inputMode === 'manual'
            ? <ManualScanner active={phase === 'scanning'} onScan={handleScan} isOnline={isOnline} mode={config.mode} />
            : <QRScanner active={phase === 'scanning'} onScan={handleScan} mode={config.mode} />}

        {/* Banners flotantes sobre la cámara/lector — cache primero (más
            urgente: sin esto el escaneo puede fallar). El aviso de Consulta
            se reemplaza acá por un cuadro más grande con buscador inline
            (ConsultaSearchBox) — antes, en cámara/USB, Consulta solo
            aceptaba un escaneo físico; ahora también se puede buscar por
            nombre/RUT sin cambiar de dispositivo de lectura. No se repite
            en modo Manual: esa pantalla completa YA es un buscador (ver
            ManualScanner), mostrar este encima sería redundante. Se oculta
            apenas `phase` deja "scanning" (un resultado ya está en
            camino) — igual queda tapado por el overlay del resultado
            (zIndex 4), pero así no se queda con el input enfocado detrás. */}
        {(cacheBanner || (isConsultaActive && phase === 'scanning')) && (
          <VStack position="absolute" top={3} left={3} right={3} zIndex={2} spacing={2} align="stretch">
            {cacheBanner}
            {isConsultaActive && phase === 'scanning' && inputMode !== 'manual' && (
              <ConsultaSearchBox onResolved={handleScan} onCancel={handleToggleConsulta} />
            )}
          </VStack>
        )}

        {/* Validando QR */}
        {phase === 'validating' && (
          <Box position="absolute" inset={0} bg={getShellBg(config.mode)} zIndex={3}
            display="flex" alignItems="center" justifyContent="center">
            <WarmShapes mode={config.mode} />
            <VStack spacing={3} bg="white" px={8} py={6} borderRadius="18px" boxShadow="0 16px 48px -8px rgba(0,0,0,.4)"
              position="relative" zIndex={1}>
              <Spinner size="xl" color="coral.500" thickness="4px" />
              <Text color="ink.700" fontSize="13.5px" fontWeight="600">
                {isOnline ? 'Validando código QR…' : 'Buscando en cache local…'}
              </Text>
            </VStack>
          </Box>
        )}

        {/* Resultado del escaneo (preview / confirmando / error) */}
        {showingResult && (
          <Box position="absolute" inset={0} bg={getShellBg(config.mode)} zIndex={4}
            display="flex" flexDirection="column" px={3} py={2} overflowY="auto" sx={{ WebkitOverflowScrolling: 'touch' }}>
            <WarmShapes mode={config.mode} />
            {/* Posicionado absoluto a propósito — así NO ocupa espacio en el
                flujo y no empuja la tarjeta hacia abajo (eso dejaba mucho más
                aire arriba que abajo). La tarjeta queda perfectamente
                centrada verticalmente por el m="auto" de abajo. */}
            {phase !== 'confirming' && (
              <IconButton
                icon={<FiX />}
                aria-label="Cerrar y volver a escanear"
                title="Cerrar y volver a escanear"
                onClick={goToScanning}
                isRound
                size="sm"
                position="absolute"
                top={3}
                right={3}
                bg="whiteAlpha.900"
                color="ink.700"
                _hover={{ bg: 'white', color: 'coral.500' }}
                boxShadow="0 4px 14px -2px rgba(0,0,0,.35)"
                zIndex={6}
              />
            )}
            <VStack spacing={3} w="100%" maxW={{ base: '420px', md: '820px' }} m="auto" py={2} position="relative" zIndex={1}>
              {phase !== 'error' && isOfflineResult && (
                <HStack px={3} py={2} bg="butter.bg" borderRadius="10px" spacing={2} w="100%" maxW={{ base: '400px', md: '780px' }}>
                  <Icon as={FiWifiOff} color="butter.ink" boxSize={4} flexShrink={0} />
                  <Text fontSize="13.5px" color="butter.ink" fontWeight="600">
                    Datos del cache — se sincronizará al reconectar
                  </Text>
                </HStack>
              )}
              <StudentInfoCard
                student={student}
                scanMode={config.mode}
                location={config.location}
                autoCountdown={autoCountdown}
                studentStatus={studentStatus}
                pedagogicalExitActive={pedagogicalExitActive}
                isEarlyExit={isEarlyExit}
                scheduledEndTime={config.config?.scheduled_end_time ?? null}
                isOfflineResult={isOfflineResult}
                onConfirm={handleConfirm}
                onConfirmExit={handleConfirmWithReason}
                onCreatePedagogicalExit={handleCreatePedagogicalExit}
                onCreateSickExit={handleCreateSickExit}
                onStatusOverride={handleConfirm}
                onOpenPickupSurvey={onOpenPickupSurvey}
                pickupSurveyReady={!!pickupSurveyDraft}
                onViewPickupSurvey={() => {
                  if (pickupSurveyPreview?.id) { openFichaView(pickupSurveyPreview.id, 'pickup'); return; }
                  setActivePreview(buildPreviewFromDraft(pickupSurveyDraft)); onOpenPreview();
                }}
                currentStaffUser={currentStaffUser}
                onOpenPedagogicalSurvey={onOpenPedagogicalSurvey}
                pedagogicalSurveyReady={pedagogicalSurveyReady}
                onViewPedagogicalSurvey={() => {
                  if (pedagogicalSurveyPreview?.id) { openFichaView(pedagogicalSurveyPreview.id, 'pedagogical'); return; }
                  setActivePreview(buildPreviewFromDraft(pedagogicalSurveyDraft)); onOpenPreview();
                }}
                onConfirmPedagogicalActive={handleConfirmPedagogicalActive}
                pedagogicalEncargado={pedagogicalEncargado}
                onSelectPedagogicalEncargado={setPedagogicalEncargado}
                pedagogicalAuthorizedBy={pedagogicalAuthorizedBy}
                onSelectPedagogicalAuthorizedBy={setPedagogicalAuthorizedBy}
                onOpenSickSurvey={onOpenSickSurvey}
                sickSurveyReady={sickSurveyReady}
                onViewSickSurvey={() => {
                  if (sickSurveyPreview?.id) { openFichaView(sickSurveyPreview.id, 'sick'); return; }
                  setActivePreview(buildPreviewFromDraft(sickSurveyDraft)); onOpenPreview();
                }}
                onConfirmSickActive={handleConfirmSickActive}
                sickEncargado={sickEncargado}
                onSelectSickEncargado={setSickEncargado}
                sickAuthorizedBy={sickAuthorizedBy}
                onSelectSickAuthorizedBy={setSickAuthorizedBy}
                onCancel={goToScanning}
                confirming={phase === 'confirming'}
                error={phase === 'error' ? scanError : null}
              />
            </VStack>
          </Box>
        )}

        {/* Resumen tras confirmar una salida cuya ficha se acaba de firmar
            (Retiro/Salida Pedagógica/Salida por Enfermedad) — ver _doLog. */}
        {phase === 'summary' && exitSummary && (
          <Box position="absolute" inset={0} bg={getShellBg(config.mode)} zIndex={4}
            display="flex" flexDirection="column" px={3} py={2} overflowY="auto" sx={{ WebkitOverflowScrolling: 'touch' }}>
            <WarmShapes mode={config.mode} />
            <IconButton
              icon={<FiX />}
              aria-label="Cerrar y volver a escanear"
              title="Cerrar y volver a escanear"
              onClick={goToScanning}
              isRound
              size="sm"
              position="absolute"
              top={3}
              right={3}
              bg="whiteAlpha.900"
              color="ink.700"
              _hover={{ bg: 'white', color: 'coral.500' }}
              boxShadow="0 4px 14px -2px rgba(0,0,0,.35)"
              zIndex={6}
            />
            <ExitSummaryView summary={exitSummary} onDone={goToScanning} />
          </Box>
        )}

        {/* Ficha "Retiro Anticipado" — se completa al elegir el motivo
            "Retiro" en una salida anticipada. deferSubmit=true: NO crea el
            survey_response al guardar, solo arma el borrador
            (pickupSurveyDraft) — recién se envía de verdad al servidor al
            tocar "Confirmar salida anticipada" (ver handleConfirmWithReason). */}
        {student && (
          <ModalAddSurveyResponse
            isOpen={isPickupSurveyOpen}
            onClose={onClosePickupSurvey}
            customer={{
              username:   student.rut,
              first_name: student.first_name,
              last_name:  student.last_name,
              email:      student.email,
              meta_data:  student.meta_data,
              id:         student.wp_user_id,
            }}
            directAccess="retiros"
            deferSubmit
            onDraftReady={(draft) => {
              setPickupSurveyDraft(draft);
              setPickupSurveyPreview(null);
            }}
          />
        )}

        {/* Ficha "Salida Pedagógica - Responsable" — se completa en la
            tarjeta "SALIDA PEDAGÓGICA" (ya programada/activa). deferSubmit=
            true por el mismo motivo que Retiro — el envío real y el cache
            por grupo ocurren en handleConfirmPedagogicalActive. */}
        {student && (
          <ModalAddSurveyResponse
            isOpen={isPedagogicalSurveyOpen}
            onClose={onClosePedagogicalSurvey}
            customer={{
              username:   student.rut,
              first_name: student.first_name,
              last_name:  student.last_name,
              email:      student.email,
              meta_data:  student.meta_data,
              id:         student.wp_user_id,
            }}
            directAccess="salidas-pedagogicas"
            deferSubmit
            pedagogicalEncargado={pedagogicalEncargado}
            pedagogicalAuthorizedBy={pedagogicalAuthorizedBy ?? currentStaffUser}
            pedagogicalExitInfo={{ title: studentStatus?.title ?? null, endsAt: studentStatus?.ends_at ?? null }}
            onDraftReady={(draft) => {
              setPedagogicalSurveyDraft(draft);
              setPedagogicalSurveyPreview(null);
            }}
          />
        )}

        {/* Ficha "Salida por Enfermedad - Responsable" — se completa en la
            tarjeta "SALIDA POR ENFERMEDAD" (ya programada/activa). Mismo
            patrón deferSubmit que Salida Pedagógica — el envío real ocurre
            en handleConfirmSickActive. */}
        {student && (
          <ModalAddSurveyResponse
            isOpen={isSickSurveyOpen}
            onClose={onCloseSickSurvey}
            customer={{
              username:   student.rut,
              first_name: student.first_name,
              last_name:  student.last_name,
              email:      student.email,
              meta_data:  student.meta_data,
              id:         student.wp_user_id,
            }}
            directAccess="salidas-enfermedad"
            deferSubmit
            pedagogicalEncargado={sickEncargado}
            pedagogicalAuthorizedBy={sickAuthorizedBy ?? currentStaffUser}
            pedagogicalExitInfo={{ title: studentStatus?.title ?? null, endsAt: null }}
            onDraftReady={(draft) => {
              setSickSurveyDraft(draft);
              setSickSurveyPreview(null);
            }}
          />
        )}

        {/* Preview de solo lectura de la ficha ya guardada — nada de esto es
            definitivo hasta tocar el botón de confirmar; solo deja revisar
            lo que quedó cargado antes de confirmar la salida. Firma/archivo
            se muestran como imagen sobre fondo blanco (el trazo suele ser
            oscuro sobre transparente — sin ese fondo casi no se ve), en vez
            de la ruta/URL como texto plano. */}
        <Modal isOpen={isPreviewOpen} onClose={onClosePreview} size="md" isCentered>
          <ModalOverlay />
          <ModalContent fontFamily="jakarta" borderRadius="20px" overflow="hidden">
            <Box bg="coral.tint" borderBottom="1px solid" borderColor="coral.soft" px={6} py={4}>
              <HStack spacing={3}>
                <Flex w="40px" h="40px" borderRadius="12px" bg="white" align="center" justify="center" flexShrink={0}
                  boxShadow="0 2px 8px -2px rgba(0,0,0,.12)">
                  <Icon as={FiCheckCircle} boxSize={5} color="coral.600" />
                </Flex>
                <Box minW={0}>
                  <Text fontSize="16px" fontWeight="800" color="ink.900" noOfLines={1}>
                    {activePreview?.survey?.name ?? 'Ficha completada'}
                  </Text>
                  <Text fontSize="12px" color="coral.700" fontWeight="600">Ficha completada · solo lectura</Text>
                </Box>
              </HStack>
            </Box>
            <ModalCloseButton top="14px" />
            <ModalBody bg="cream.bg" px={5} py={5}>
              <VStack align="stretch" spacing="10px">
                {(activePreview?.responses ?? []).map((r, idx) => {
                  const typeName = r.question?.type?.name;
                  const isImage  = typeName === 'signature' || typeName === 'file';
                  const isBoolean = typeName === 'boolean';
                  const isYes = ['1', 1, true, 'true', 'on'].includes(r.answer);
                  return (
                    <Box key={r.id ?? idx} bg="white" borderRadius="14px" border="1px solid" borderColor="cream.line" p={3}>
                      <Text fontSize="10.5px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".06em" mb="6px">
                        {r.question?.text ?? '—'}
                      </Text>
                      {isImage ? (
                        r.answer
                          ? (
                            <Box bg="white" border="1.5px solid" borderColor="cream.line2" borderRadius="10px" p={2}>
                              <Image src={buildFileUrl(r.answer, API_URL_BASE)} maxH="180px" mx="auto" objectFit="contain" borderRadius="6px" />
                            </Box>
                          )
                          : <Text fontSize="14px" color="ink.400">—</Text>
                      ) : isBoolean ? (
                        <Box as="span" px="10px" py="3px" borderRadius="999px" fontSize="12.5px" fontWeight="700"
                          bg={isYes ? 'mint.bg' : 'rose.bg'} color={isYes ? 'mint.ink' : 'rose.ink'}>
                          {isYes ? 'Sí' : 'No'}
                        </Box>
                      ) : (
                        <Text fontSize="14px" color="ink.900" fontWeight="600">{r.answer || '—'}</Text>
                      )}
                    </Box>
                  );
                })}
                {!activePreview?.responses?.length && (
                  <Text fontSize="14px" color="ink.400" textAlign="center" py={4}>Sin datos para mostrar.</Text>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter bg="cream.bg" borderTop="1px solid" borderColor="cream.line" px={5} py={3}>
              <Button w="full" variant="ghost" size="sm" borderRadius="12px" color="ink.400" onClick={onClosePreview}>
                Cerrar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Ficha YA ENVIADA — mismo modal con cabecera/logo y botón "Ver PDF"
            que ya se usa en el Perfil del alumno (FichasTab.js), reutilizado
            tal cual. El preview simple de arriba sigue cubriendo el caso
            borrador (todavía sin id real, sin PDF posible). */}
        {student && (
          <ModalSurveyResponse
            isOpen={isFichaViewOpen}
            onClose={onCloseFichaView}
            customer={{
              username:   student.rut,
              first_name: student.first_name,
              last_name:  student.last_name,
              email:      student.email,
              meta_data:  student.meta_data,
              id:         student.wp_user_id,
            }}
            data={viewingFichaData}
            onSurveyDeleted={handleFichaDeleted}
          />
        )}
      </Box>
    </Box>

    {/* ── Panel lateral derecho: historial y cache (siempre visible, ver
        HistoryPanel) ── */}
    <HistoryPanel
      collapsed={historyCollapsed}
      onToggleCollapse={toggleHistoryCollapsed}
      refreshToken={history.length}
      cacheSize={cacheSize}
      cacheLoading={cacheLoading}
      lastSyncDate={lastSyncDate}
      refreshCache={refreshCache}
      navbarOffset={NAVBAR_OFFSET}
    />

    <ScannerConfig isOpen={isConfigOpen} onClose={onCloseConfig}
      currentScannerId={config?.scanner_id ?? null} onSelect={handleSelectConfig}
      inputMode={inputMode} initialRemember={config ? remembered : false} />

    {/* Picker chico para desempatar cuando el switch Entrada/Salida o el
        botón de Consulta encuentran 2+ lectores activos del mismo tipo sin
        un "último usado" todavía (ver resolveCategory) — salta directo a la
        lista de esa categoría, sin la grilla completa ni "Recordar este
        lector" (ver forcedCategory en ScannerConfig.js). */}
    <ScannerConfig isOpen={!!pendingPickerCategory} onClose={() => setPendingPickerCategory(null)}
      currentScannerId={config?.scanner_id ?? null} onSelect={handlePendingPickerSelect}
      inputMode={inputMode} initialRemember={false} forcedCategory={pendingPickerCategory} />

    <style>{`
      @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    `}</style>
    </Box>
  );
};

export default ScannerShell;
